import SharePanel from "./components/SharePanel";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import "@/App.css";
import axios from "axios";
import CRT from "@/components/CRT";
import Remote from "@/components/Remote";
import MobileTouchRemote from "@/components/MobileTouchRemote";
import UploadTerminal from "@/components/UploadTerminal";
import OwnerBar from "@/components/OwnerBar";
import ViewerSettings from "@/components/ViewerSettings";
import ChannelGuide from "@/components/ChannelGuide";
import MaintenanceOverlay from "@/components/MaintenanceOverlay";
import NoticeOverlay from "@/components/NoticeOverlay";
import TributeView, { TributeBanner } from "@/components/TributeView";
import MobileWarning from "@/components/MobileWarning";
import UpdatesOverlay from "@/components/UpdatesOverlay";
import ChannelBrowse from "@/components/ChannelBrowse";
import TapeDetail from "@/components/TapeDetail";
import { getWatched, markWatched } from "@/lib/watched";
import { listFavourites } from "@/lib/favourites";
import { expandChannelsWithShows } from "@/lib/subchannels";
import { getAckedNoticeId, ackNotice } from "@/lib/notice";
import { shareUrl } from "@/lib/share";
import { useTheme } from "@/lib/theme";
import { getPrefs, patchPrefs } from "@/lib/viewerPrefs";
import { useAuth } from "@/lib/auth";
import { startBeaconLoop, setSignedIn as setBeaconSignedIn, setAnalyticsEnabled, trackTune, resetTune, trackView, trackWatch } from "@/lib/statsBeacon";
import { useChannelDigitBuffer } from "@/lib/useChannelDigitBuffer";
import ContactPanel from "@/components/ContactPanel";
import { Toaster, toast } from "sonner";
import { unlock as unlockTrophy, hydrateFromServer as hydrateTrophiesFromServer } from "@/lib/trophies";
import TrophyRoom from "@/components/TrophyRoom";
import FirstVisitTour from "@/components/FirstVisitTour";
import { SeasonalOverlay } from "@/components/SeasonalOverlay";
import { installConsoleGuard } from "@/lib/consoleGuard";
import SeriesInfo from "./components/SeriesInfo";
import WhatsNew from "./components/WhatsNew";

// Overlay wrapper — decides whether an in-tube overlay (guide / upload /
// updates) renders inline as a CRT child (bezel mode) or gets portaled
// to `document.body` inside a `position: fixed` viewport-filling layer
// (fullscreen mode). The portal is necessary because `.crt-curve` uses
// `transform: perspective(...)` which turns it into a containing block
// for `position: fixed` descendants — so a pure-CSS "escape the bezel"
// approach can't work; the overlay has to physically leave the CRT
// subtree via `createPortal`. In bezel mode we just render children
// inline, preserving the classic in-tube overlay behaviour untouched.
function FullscreenPortal({ fullscreen, children, reserveTopChrome = false }) {
  if (!fullscreen) return children;
  return createPortal(
    <div
      className={`fixed inset-x-0 bottom-0 z-40 bg-black ${reserveTopChrome ? "top-[64px]" : "top-0"}`}
      style={{ fontFamily: "JetBrains Mono, monospace" }}
      data-testid="fullscreen-overlay-layer"
    >
      {children}
    </div>,
    document.body
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const OWNER_KEY_STORAGE = "crt_owner_token";
const VOLUME_STORAGE = "crt_volume";
const SCANLINE_STORAGE = "crt_scanline_level";
const MAX_VOLUME = 10;
const SCANLINE_LABELS = ["OFF", "NORMAL", "HEAVY"];

const FAV_ID = "__fav__";
// Virtual channel id for the in-CRT contact form. Always appended to the
// lineup so viewers always have a way to reach the station, even if no
// real channels have content yet (e.g. brand-new cabinet).
const CONTACT_ID = "__contact__";

function getStoredToken() {
  // localStorage can throw in sandboxed iframes / private mode — silent fallback is intentional
  try { return localStorage.getItem(OWNER_KEY_STORAGE) || ""; } catch (e) { return ""; }
}

function normaliseVolume(value, fallback = 6) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;

  // Phase 2.3.61h.5 — legacy PREFS stored volume on a 0..100 scale.
  // The live SCARTLEAD.TV player uses 0..10. Values above 10 are migrated
  // once by dividing by ten, then the canonical 0..10 integer is clamped.
  const migrated = (n > MAX_VOLUME && n <= 100) ? (n / 10) : n;
  return Math.max(0, Math.min(MAX_VOLUME, Math.round(migrated)));
}

function getStoredVolume() {
  // Same intentional silent fallback as getStoredToken
  try {
    const raw = localStorage.getItem(VOLUME_STORAGE);
    if (raw !== null) return normaliseVolume(raw, 6);
  } catch (e) { /* intentional: see getStoredToken */ }
  return 6;
}

function getStoredScanlineLevel() {
  try {
    const v = parseInt(localStorage.getItem(SCANLINE_STORAGE) || "", 10);
    if (Number.isFinite(v) && v >= 0 && v <= 2) return v;
  } catch (e) { /* see getStoredToken */ }
  return 1; // default = normal scanlines, matching the prior visual
}

function authHeaders() {
  const t = getStoredToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function App() {
  const { theme } = useTheme();
  const { user: authUser } = useAuth();
  // Station-wide loudness target. Owner edits this in OwnerBar → AUDIO;
  // we fetch the current value on mount + whenever the owner saves a new
  // one (custom event). Defaults to -16 LUFS if the request fails so the
  // Cosmetic dev-console deterrents — installed once on mount for
  // the lifetime of the app. Swallows F12 / Ctrl+Shift+I / Cmd+Opt+I,
  // suppresses right-click context menus outside of form inputs, and
  // prints a friendly ASCII banner in the DevTools console when the
  // heuristic detects it's been opened. See `lib/consoleGuard.js` for
  // the full disclosure on what this does and doesn't protect.
  useEffect(() => {
    const teardown = installConsoleGuard({ enabled: true });
    return teardown;
  }, []);

  // Analytics beacons — fire visit on mount, heartbeat every 30s,
  // and keep the "signed in" flag in sync so unique-visitor buckets
  // can distinguish signed-in vs anonymous. Wired here (rather than
  // per-component) because App is the only guaranteed always-mounted
  // component and its lifetime = the viewer's session.
  useEffect(() => {
    setBeaconSignedIn(!!authUser);
  }, [authUser]);


  // CRT still computes sensible per-video gains.
  const [audioTargetLufs, setAudioTargetLufs] = useState(-16);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/site/audio-settings`);
        if (!cancelled && typeof res.data?.target_lufs === "number") {
          setAudioTargetLufs(res.data.target_lufs);
        }
      } catch (e) { /* keep default */ }
    };
    load();
    const onUpdated = () => load();
    window.addEventListener("crt:audio-settings-updated", onUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener("crt:audio-settings-updated", onUpdated);
    };
  }, []);
  // Pledge-drive config (Buy Me A Coffee link + trigger frequency +
  // localised copy). Fetched once on mount and refreshed whenever the
  // OwnerBar re-saves the panel (which dispatches
  // `crt:pledge-drive-updated`). The CRT component picks up the shape
  // and randomly fires a "SUPPORT THE STATION" interstitial roughly
  // once every `frequency` channel changes when `enabled` is true.
  const [pledgeDrive, setPledgeDrive] = useState({ enabled: false, url: "", frequency: 10 });
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/site/pledge-drive`);
        if (!cancelled && res.data && typeof res.data === "object") {
          setPledgeDrive(res.data);
        }
      } catch (e) { /* keep default */ }
    };
    load();
    const onUpdated = () => load();
    window.addEventListener("crt:pledge-drive-updated", onUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener("crt:pledge-drive-updated", onUpdated);
    };
  }, []);
  // "+N NEW TAPES" callout for the welcome bumper. Fetched once when a
  // signed-in user is detected; null otherwise so the bumper hides the
  // line entirely instead of showing "+0 NEW TAPES" (which would feel like
  // a bug). Best-effort — a failed fetch just leaves the line out.
  const [whatsNew, setWhatsNew] = useState(null);
  // Tribute / In Memoriam. `tribute` = whatever the backend has
  // published; `showTribute` toggles the fullscreen viewer page.
  // Refetch on the `crt:tribute-updated` window event so the owner's
  // PUBLISH click updates every consumer (banner freshness, view) live
  // without a reload.
  const [tribute, setTribute] = useState(null);
  const [showTribute, setShowTribute] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/site/tribute`);
        if (!cancelled) setTribute(r.data || null);
      } catch (e) { /* noop */ }
    };
    load();
    const onUpdated = (e) => { setTribute(e.detail || null); };
    window.addEventListener("crt:tribute-updated", onUpdated);
    return () => { cancelled = true; window.removeEventListener("crt:tribute-updated", onUpdated); };
  }, []);
  useEffect(() => {
    if (!authUser) { setWhatsNew(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/viewer/whats-new`, {
          credentials: "include",
        });
        if (!res.ok || cancelled) return;
        const body = await res.json();
        if (!cancelled && body && typeof body.count === "number" && body.count > 0) {
          setWhatsNew(body);
        }
      } catch (e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [authUser]);
  // Viewer-personal prefs hook. Re-reads from localStorage whenever the
  // viewer applies a change via the ViewerSettings panel, so prefs take
  // effect without a reload (volume, reduce_motion, silent_auto_skip
  // all flip live). `starting_channel_id` is only read at mount —
  // switching it changes nothing about the current tape.
  const [viewerPrefs, setViewerPrefs] = useState(getPrefs);
  const PICTURE_MODES = ["letterbox", "14:9", "pan-scan", "cinema-zoom", "stretch"];
  const pictureMode = PICTURE_MODES.includes(viewerPrefs.picture_mode)
    ? viewerPrefs.picture_mode
    : "letterbox";

  const cyclePictureMode = useCallback(() => {
    const currentPrefs = getPrefs();
    const current = PICTURE_MODES.includes(currentPrefs.picture_mode)
      ? currentPrefs.picture_mode
      : "letterbox";
    const next = PICTURE_MODES[(PICTURE_MODES.indexOf(current) + 1) % PICTURE_MODES.length];
    patchPrefs({ picture_mode: next });
  }, []);
  useEffect(() => {
    const onPrefs = (e) => {
      if (e && e.detail) setViewerPrefs(e.detail);
      else setViewerPrefs(getPrefs());
    };
    window.addEventListener("crt:prefs-updated", onPrefs);
    return () => window.removeEventListener("crt:prefs-updated", onPrefs);
  }, []);
  // Owner edits to categories (add/rename/reorder/delete) fire this event.
  // Rebuild the lineup so the channel guide picks up the new rails without
  // a page reload.
  const fetchChannelsRef = useRef(null);
  useEffect(() => {
    const onCats = () => { fetchChannelsRef.current && fetchChannelsRef.current(); };
    window.addEventListener("crt:categories-updated", onCats);
    // Same channel refresh path when videos are batch-mutated server-side
    // (e.g. MEGA auto-posters). Reuses the ref so the callback always
    // points at the latest fetchChannels closure.
    const onVideosUpdated = () => { fetchChannelsRef.current && fetchChannelsRef.current(); };
    window.addEventListener("crt:videos-updated", onVideosUpdated);
    return () => {
      window.removeEventListener("crt:categories-updated", onCats);
      window.removeEventListener("crt:videos-updated", onVideosUpdated);
    };
  }, []);

  // ── Remote ↔ CRT play/pause bridge ──────────────────────────────────
  // CRT.js dispatches `crt:playback-state` whenever the active player
  // pauses or resumes (covers native <video>, YT, archive). We mirror it
  // into state so the remote's Play/Pause button can flip its icon
  // between ▶ and ❚❚ instantly. Tapping the button dispatches
  // `crt:play-pause` which CRT.js listens for to toggle the player.
  const [remotePaused, setRemotePaused] = useState(false);
  useEffect(() => {
    const onState = (e) => setRemotePaused(!!(e && e.detail && e.detail.paused));
    window.addEventListener("crt:playback-state", onState);
    return () => window.removeEventListener("crt:playback-state", onState);
  }, []);
  const [statsMediaPlaying, setStatsMediaPlaying] = useState(false);
  useEffect(() => {
    const onPlaying = (e) => setStatsMediaPlaying(!!(e && e.detail && e.detail.playing));
    window.addEventListener("crt:analytics-playing", onPlaying);
    return () => window.removeEventListener("crt:analytics-playing", onPlaying);
  }, []);
  const handleRemotePlayPause = useCallback(() => {
    window.dispatchEvent(new CustomEvent("crt:play-pause"));
  }, []);
  // CRT power persists across REFRESH but resets when the TAB / BROWSER
  // closes — `sessionStorage` is exactly this contract. So:
  //   * brand-new tab → power off → viewer presses POWER → guide opens
  //   * F5 / hot reload  → power stays on, broadcast resumes
  //   * close + reopen   → power off again, the boot ritual replays
  // localStorage was wrong for this because it survived browser restarts;
  // sessionStorage gives us the "within this sitting" boundary the
  // owner asked for.
  const [power, setPower] = useState(() => {
    try {
      return sessionStorage.getItem("crt_power_v1") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try { sessionStorage.setItem("crt_power_v1", power ? "1" : "0"); } catch { /* ignore */ }
  }, [power]);
  const [channels, setChannels] = useState([]);
  const [videosByChannel, setVideosByChannel] = useState({});
  // Sky Q-style channel categories (MOVIES / MUSIC / KIDS…) used to bucket
  // the lineup into rails inside ChannelGuide. Fetched alongside channels;
  // owner edits broadcast `crt:categories-updated` to trigger a refresh
  // without a page reload.
  const [categories, setCategories] = useState([]);
  // Flips to `true` the first time `fetchChannels()` resolves. Used to gate
  // the NO SIGNAL placeholder inside the CRT so a hard refresh doesn't flash
  // it for the ~200-500 ms while the lineup is still hydrating. Without this
  // gate viewers saw "NO SIGNAL" → SHOW STARTING SOON → tape, which felt
  // like the test card was running twice.
  const [lineupReady, setLineupReady] = useState(false);
  const [channelIndex, setChannelIndex] = useState(0);
  const [videoIndex, setVideoIndex] = useState(0);
  // Initialise volume from viewer prefs (which beats the legacy
  // getStoredVolume on first mount; legacy is kept as the fallback inside
  // getPrefs via DEFAULT_PREFS.volume).
  const [volume, setVolume] = useState(() => {
    const p = getPrefs();
    const fallback = getStoredVolume();
    if (typeof p.volume === "number") return normaliseVolume(p.volume, fallback);
    return fallback;
  });
  const [isStatic, setIsStatic] = useState(false);
  const [mode, setMode] = useState("tv"); // 'tv' | 'upload' | 'guide' | 'updates' | 'browse' | 'detail'
  // Phase 2.3.61e.2-r2 — remember the exact live tune when Guide opens.
  // Browsing the Guide must never mutate this snapshot; closing restores it
  // defensively without invoking the tuner/static path.
  const guideEntryRef = useRef(null);
  // Which channel the operator is browsing (grid view of every tape on
  // that channel), and which tape they're inspecting in the TMDb-style
  // detail page. Both live at App level so mode transitions from
  // guide → browse → detail preserve their targets and ESC unwinds
  // cleanly without a full state reset.
  const [browseChannelId, setBrowseChannelId] = useState(null);
  const [detailVideoId, setDetailVideoId] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  // Owner-controlled site setting — "bezel" (classic overlays inside the
  // CRT tube) or "fullscreen" (guide/upload/updates escape the bezel and
  // fill the viewport). Fetched from `/api/site/layout` on boot; owner
  // toggles via ViewerSettings. Affects every viewer, not just owner.
  const [layoutMode, setLayoutMode] = useState("bezel");
  const [osdChannel, setOsdChannel] = useState(false);
  const [osdVolume, setOsdVolume] = useState(false);

  // Remote-only volume OSD.
  // Kept deliberately separate from legacy osdVolume because that path is
  // coupled to CRT banner behaviour. Remote volume must never summon, reset
  // or flicker NOW BROADCASTING.
  const [remoteVolumeOsd, setRemoteVolumeOsd] = useState(null);
  const remoteVolumeOsdTimer = useRef(null);
  const [isOwner, setIsOwner] = useState(false);
  const [ownerChecked, setOwnerChecked] = useState(false);
  // Phase 2.3.69 — analytics start only after owner detection resolves.
  useEffect(() => {
    if (!ownerChecked) { setAnalyticsEnabled(false); return undefined; }
    const enabled = !isOwner;
    setAnalyticsEnabled(enabled);
    if (!enabled) return () => setAnalyticsEnabled(false);
    const teardown = startBeaconLoop();
    return () => { try { teardown && teardown(); } catch (_) {} setAnalyticsEnabled(false); resetTune(); };
  }, [ownerChecked, isOwner]);

  const [watchedTick, setWatchedTick] = useState(0);
  const [maintenance, setMaintenance] = useState(false);
  // Phase 2.3.69-r1.1 — tune analytics must be declared only after
  // every dependency exists. In r1 this effect appeared before `maintenance`
  // was initialized, causing a render-time temporal-dead-zone ReferenceError.
  useEffect(() => {
    const ch = channels[channelIndex];
    if (!ownerChecked || isOwner || !power || maintenance || !ch || !ch.id || ch.virtual) {
      resetTune();
      return;
    }
    trackTune(ch.id);
  }, [ownerChecked, isOwner, power, maintenance, channelIndex, channels]);

  // Fail-closed startup gate: viewer chrome stays hidden until /site/status succeeds.
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState("");
  const [maintenanceReturnsAt, setMaintenanceReturnsAt] = useState(null);
  // Phase 2.3.68-r2.2 — true only when maintenance itself woke an
  // otherwise powered-off cabinet. This is display-only ownership:
  // no media may start, and the cabinet returns OFF when maintenance ends.
  const maintenanceForcedPowerRef = useRef(false);
  // "Click to continue" notice board — owner-broadcast announcement that
  // viewers must dismiss before videos play. `notice` is the server payload
  // (or null/disabled); `noticeAckedId` is the last id the *local* viewer
  // dismissed. The overlay renders iff notice is enabled AND noticeAckedId
  // !== notice.notice_id.
  const [notice, setNotice] = useState(null);
  const [noticeAckedId, setNoticeAckedId] = useState(() => getAckedNoticeId());
  // System gates (maintenance + un-acked notice) now render INSIDE the
  // CRT screen, which means they're invisible if the cabinet is off. To
  // preserve the previous "you can't miss this" behaviour we force-power
  // the TV on as soon as a gate becomes active. Doesn't auto-power-off
  // when the gate clears — the viewer keeps watching whatever they were.
  // Owners are exempt from the maintenance auto-power because they see a
  // small banner instead of the full overlay.
  useEffect(() => {
    const hasUnackedNotice = !!(notice && notice.enabled && noticeAckedId !== notice.notice_id);
    const maintenanceGate = maintenance && !isOwner;
    const hasGate = maintenanceGate || hasUnackedNotice;

    if (maintenanceGate && !power) {
      maintenanceForcedPowerRef.current = true;
      setPower(true);
      return;
    }

    // If maintenance borrowed CRT power only to display the stand-by
    // slate, restore the original OFF state when maintenance clears.
    if (!maintenanceGate && maintenanceForcedPowerRef.current) {
      maintenanceForcedPowerRef.current = false;
      if (power) setPower(false);
      return;
    }

    // Notices retain their established auto-power behaviour.
    if (hasGate && !power) setPower(true);
  }, [maintenance, isOwner, notice, noticeAckedId, power]);
  // Small-viewport warning. We track narrowness reactively (rotate / resize
  // updates the gate) and a session-scoped dismiss flag so the warning is
  // re-shown next time the user opens the tab. Threshold 1024px catches
  // phones AND tablets in portrait — both are too cramped for the CRT.
  const SMALL_VIEWPORT_PX = 1024;
  const SESSION_DISMISS_KEY = "crt_mobile_warning_dismissed";
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < SMALL_VIEWPORT_PX : false
  );
  const [mobileDismissed, setMobileDismissed] = useState(() => {
    try { return sessionStorage.getItem(SESSION_DISMISS_KEY) === "1"; } catch (e) { return false; }
  });
  // Owner-editable copy for the mobile warning (defaults applied server-side).
  const [mobileWarning, setMobileWarning] = useState({ title: "", body: "" });
  const [viewerTips, setViewerTips] = useState({
    title: "VIEWER TIPS",
    sound_line: "CLICK ANYWHERE TO ENABLE SOUND",
  });
  // Owner-editable subtitle line for the WELCOME BACK bumper. Fetched
  // once on boot from /api/site/welcome-message (public). Falls back to
  // a default server-side when unset.
  const [welcomeMessage, setWelcomeMessage] = useState({ subtitle: "" });
  // Live per-channel viewer counts pushed by the lobby WS via a CRT-side
  // event. Used by ChannelGuide to render "📺 N WATCHING" badges. The
  // payload is keyed by channel UUID → integer count; channels with
  // zero current viewers are omitted by the server.
  const [channelViewerCounts, setChannelViewerCounts] = useState({});
  // Phase 2.3.68-r2 — immediate station-status propagation from the
  // existing global lobby WebSocket. The 15s /site/status poll below is
  // intentionally retained as recovery/reconciliation fallback.
  useEffect(() => {
    const onSiteStatus = (e) => {
      const next = (e && e.detail) || null;
      if (!next || typeof next !== "object") return;
      setMaintenance(!!next.maintenance);
      setMaintenanceMsg(next.message || "");
      setMaintenanceReturnsAt(next.returns_at || null);
      setMaintenanceChecked(true);
    };
    window.addEventListener("crt:site-status", onSiteStatus);
    return () => window.removeEventListener("crt:site-status", onSiteStatus);
  }, []);

  useEffect(() => {
    const onCounts = (e) => {
      const next = (e && e.detail) || {};
      if (next && typeof next === "object") setChannelViewerCounts(next);
    };
    window.addEventListener("crt:channel-counts", onCounts);
    return () => window.removeEventListener("crt:channel-counts", onCounts);
  }, []);

  // Welcome-back data, dispatched by CRT.js the moment the bumper would
  // have fired (once per signed-in session). We render it as an inline
  // banner at the top of the ChannelGuide instead of as a separate
  // overlay so it never gets occluded by the guide opening over it
  // (the old z-40-vs-z-40 collision the owner reported). `null` = no
  // banner; a payload = render the banner.
  const [welcomeBackData, setWelcomeBackData] = useState(null);
  useEffect(() => {
    const onShow = (e) => setWelcomeBackData((e && e.detail) || null);
    const onHide = () => setWelcomeBackData(null);
    window.addEventListener("crt:welcome-back-show", onShow);
    window.addEventListener("crt:welcome-back-hide", onHide);
    return () => {
      window.removeEventListener("crt:welcome-back-show", onShow);
      window.removeEventListener("crt:welcome-back-hide", onHide);
    };
  }, []);
  // Welcome-back data is retained until the viewer opens the Guide themselves.
  // The Guide is never auto-opened: viewers choose when they are ready to browse.
  const dismissWelcomeBack = useCallback(() => {
    setWelcomeBackData(null);
    try {
      window.dispatchEvent(new CustomEvent("crt:welcome-back-dismiss"));
    } catch (e) { /* noop */ }
  }, []);
  // "What's New" updates panel — triggered by the UPDATES button on the
  // remote. Lazy-fetched on click so we always show the latest list of
  // recent videos, then opened by switching to `mode === "updates"` which
  // renders the CHECKING-FOR-UPDATES → WHATS-NEW boot sequence INSIDE
  // the CRT screen (no full-page overlay).
  const [updatesData, setUpdatesData] = useState({ notes: "", update_id: "", features: [], recent_videos: [] });
  // Phase 2.3.61e.5.1-r2 — viewer-facing update notification.
  // The backend owns update_id; this browser stores only the last one opened.
  const [updatesAvailable, setUpdatesAvailable] = useState(false);
  // Monotonic counter — bumped by the SELECT button on the remote so CRT
  // can re-summon the NOW BROADCASTING banner + COMING UP strip on demand
  // (without going through a channel-change sequence). Watched by an effect
  // in CRT.js.
  const [selectTick, setSelectTick] = useState(0);
  // Phase 2.3.62.6-r5 - explicit same-channel live programme handoff signal.
  // Kept separate from SELECT and channel tuning/static.
  const [programmeHandoffTick, setProgrammeHandoffTick] = useState(0);
  // Bumped by the SELECT button's LONG-press to surface a quick full-EPG
  // peek overlay (channel + currently-broadcasting tape title), driven by
  // an effect in CRT.js that auto-dismisses after 6s.
  const [peekTick, setPeekTick] = useState(0);
  // Scanline intensity: 0 = off, 1 = normal (legacy default), 2 = heavy.
  // Cycled by long-pressing POWER on the remote. Persisted to localStorage.
  const [scanlineLevel, setScanlineLevel] = useState(getStoredScanlineLevel);
  const handleSelectInfo = useCallback(() => {
    setSelectTick((t) => t + 1);
  }, []);
  const handleSelectPeek = useCallback(() => {
    setPeekTick((t) => t + 1);
  }, []);
  const handleCycleScanlines = useCallback(() => {
    setScanlineLevel((lvl) => (lvl + 1) % 3);
  }, []);

  // ─── Trophy room state + fire helper ─────────────────────────────────
  // Trophies are dopamine rewards. `fireTrophy(id)` is idempotent (re-firing
  // a previously-unlocked id is a noop) so we can pepper trigger calls
  // throughout the app without worrying about double-toasts. `trophyTick`
  // is bumped on each unlock so the open TrophyRoom modal re-reads
  // localStorage and shows the freshly-unlocked badge.
  const [showTrophies, setShowTrophies] = useState(false);
  const [trophyTick, setTrophyTick] = useState(0);
  const [trophyPulse, setTrophyPulse] = useState(0);
  const [trophySettings, setTrophySettings] = useState({ disabled: [], overrides: {}, sound_url: null });
  const [showPhantom, setShowPhantom] = useState(false);
  const channelHopsRef = useRef(0);
  const visitedChannelsRef = useRef(new Set());
  const trophyAudioRef = useRef(null);
  const trophyAudioUrlRef = useRef(null);
  const trophyAudioPrimedRef = useRef(false);
  const trophyAudioPlaybackGenerationRef = useRef(0);
  const [trophyAudioRevision, setTrophyAudioRevision] = useState(0);
  // Fetch the owner's trophy settings on mount so we honour disabled flags +
  // pick up the configured confirmation sound.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/site/trophies`);
        if (!cancelled) setTrophySettings(r.data || { disabled: [], overrides: {}, sound_url: null });
      } catch (e) { /* defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // Build and preload the owner-configured trophy sound before an unlock occurs.
  // The revision query parameter is intentional: the backend serves this endpoint
  // with a one-hour public cache, while an owner can replace the sound at the same
  // stable URL during a live session. Bumping the revision guarantees the new bytes.
  const prepareTrophyAudio = useCallback(() => {
    if (!trophySettings.sound_url || typeof Audio === "undefined") return null;
    const absUrl = `${BACKEND_URL}${trophySettings.sound_url}?v=${trophyAudioRevision}`;
    if (!trophyAudioRef.current || trophyAudioUrlRef.current !== absUrl) {
      try {
        const audio = new Audio(absUrl);
        audio.preload = "auto";
        audio.load();
        trophyAudioRef.current = audio;
        trophyAudioUrlRef.current = absUrl;
        trophyAudioPrimedRef.current = false;
      } catch (e) {
        console.warn("[trophy-audio] preload failed", e);
        return null;
      }
    }
    return trophyAudioRef.current;
  }, [trophySettings.sound_url, trophyAudioRevision]);

  // Chrome/Safari may reject unmuted playback that starts outside a trusted
  // activation callstack. Follow CRT.js's established gesture pattern and prime
  // the already-preloaded trophy element synchronously on the viewer's first
  // usable pointer/key/touch gesture. Priming is muted + zero-volume, then paused
  // and rewound immediately, so the viewer never hears the confirmation early.
  useEffect(() => {
    const audio = prepareTrophyAudio();
    if (!audio || trophyAudioPrimedRef.current) return undefined;

    let active = true;
    const removeGestureListeners = () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      window.removeEventListener("touchstart", onGesture);
    };
    const restoreAfterPrime = (target, muted, volume, playbackGeneration) => {
      // A real trophy unlock may start while the muted prime Promise is still
      // resolving. Never pause/rewind/restore media state if real playback has
      // taken ownership of this Audio element in the meantime.
      if (trophyAudioPlaybackGenerationRef.current !== playbackGeneration) return false;
      try { target.pause(); } catch (e) { /* noop */ }
      try { target.currentTime = 0; } catch (e) { /* noop */ }
      target.volume = volume;
      target.muted = muted;
      return true;
    };
    const onGesture = () => {
      const target = prepareTrophyAudio();
      if (!target || trophyAudioPrimedRef.current) {
        removeGestureListeners();
        return;
      }
      const previousMuted = target.muted;
      const previousVolume = target.volume;
      const playbackGeneration = trophyAudioPlaybackGenerationRef.current;
      try {
        target.muted = true;
        target.volume = 0;
        target.currentTime = 0;
        const playback = target.play();
        if (playback && typeof playback.then === "function") {
          playback.then(() => {
            if (!active) return;
            restoreAfterPrime(target, previousMuted, previousVolume, playbackGeneration);
            trophyAudioPrimedRef.current = true;
            removeGestureListeners();
          }).catch((e) => {
            if (trophyAudioPlaybackGenerationRef.current === playbackGeneration) {
              restoreAfterPrime(target, previousMuted, previousVolume, playbackGeneration);
              console.warn("[trophy-audio] gesture prime blocked; will retry", e);
            }
          });
        } else {
          restoreAfterPrime(target, previousMuted, previousVolume, playbackGeneration);
          trophyAudioPrimedRef.current = true;
          removeGestureListeners();
        }
      } catch (e) {
        if (trophyAudioPlaybackGenerationRef.current === playbackGeneration) {
          restoreAfterPrime(target, previousMuted, previousVolume, playbackGeneration);
          console.warn("[trophy-audio] gesture prime failed; will retry", e);
        }
      }
    };

    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("keydown", onGesture);
    window.addEventListener("touchstart", onGesture);
    return () => {
      active = false;
      removeGestureListeners();
    };
  }, [prepareTrophyAudio]);

  // When the viewer signs in, hydrate their persisted trophy set from the
  // server into localStorage. This is what makes the ★ PATRON badge follow
  // a viewer across devices — a tipper who logged in on desktop no longer
  // needs to re-earn the trophy on mobile. Any anonymous unlocks earned
  // BEFORE the sign-in are pushed up to the account inside
  // hydrateFromServer() so the viewer never loses history.
  useEffect(() => {
    if (!authUser || !authUser.user_id) return;
    hydrateTrophiesFromServer().then((ok) => {
      // Bump the trophy tick so an already-open TrophyRoom modal re-reads
      // and a returning viewer's badges surface immediately without needing
      // to close/reopen. Bezel components (PatronBug) pick up the new
      // state on their next render anyway.
      if (ok) setTrophyTick((t) => t + 1);
    });
  }, [authUser?.user_id]);
  const fireTrophy = useCallback((id) => {
    if (trophySettings.disabled && trophySettings.disabled.includes(id)) return;
    const meta = unlockTrophy(id);
    if (!meta) return;
    // Apply owner override on display copy, if present.
    const ov = (trophySettings.overrides || {})[id] || {};
    const displayName = ov.name || meta.name;
    const displayDesc = ov.desc || meta.desc;
    setTrophyTick((t) => t + 1);
    setTrophyPulse((p) => p + 1);
    toast.success(`🏆 TROPHY: ${displayName}`, {
      description: displayDesc,
      duration: 4500,
    });
    // Play the preloaded/gesture-primed owner confirmation sound. Rewind so
    // rapid successive unlocks start cleanly. Do not hide playback failures: a
    // console warning makes browser-policy or media failures diagnosable without
    // changing the viewer-facing trophy success path.
    if (trophySettings.sound_url) {
      const audio = prepareTrophyAudio();
      if (audio) {
        try {
          trophyAudioPlaybackGenerationRef.current += 1;
          audio.muted = false;
          if (audio.volume === 0) audio.volume = 1;
          audio.currentTime = 0;
          const playback = audio.play();
          if (playback && typeof playback.catch === "function") {
            playback.catch((e) => console.warn("[trophy-audio] unlock playback failed", e));
          }
        } catch (e) {
          console.warn("[trophy-audio] unlock playback failed", e);
        }
      }
    }
  }, [trophySettings, prepareTrophyAudio]);

  const handleSaveTrophySettings = useCallback(async (payload) => {
    try {
      const r = await axios.post(`${API}/site/trophies`, payload, { headers: authHeaders() });
      setTrophySettings(r.data);
      toast.success("TROPHY SETTINGS SAVED");
      return true;
    } catch (e) {
      toast.error("SAVE FAILED", { description: e.response?.data?.detail || e.message });
      return false;
    }
  }, []);

  const handleUploadTrophySound = useCallback(async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/site/trophy-sound`, fd, { headers: { ...authHeaders() } });
      setTrophySettings(r.data);
      try { trophyAudioRef.current?.pause(); } catch (e) { /* noop */ }
      trophyAudioRef.current = null;
      trophyAudioUrlRef.current = null;
      trophyAudioPrimedRef.current = false;
      setTrophyAudioRevision((revision) => revision + 1); // bypass cached stable endpoint + re-arm
      toast.success("TROPHY SOUND UPLOADED");
      return true;
    } catch (e) {
      toast.error("UPLOAD FAILED", { description: e.response?.data?.detail || e.message });
      return false;
    }
  }, []);

  const handleClearTrophySound = useCallback(async () => {
    try {
      const r = await axios.delete(`${API}/site/trophy-sound`, { headers: authHeaders() });
      setTrophySettings(r.data);
      try { trophyAudioRef.current?.pause(); } catch (e) { /* noop */ }
      trophyAudioRef.current = null;
      trophyAudioUrlRef.current = null;
      trophyAudioPrimedRef.current = false;
      toast.success("TROPHY SOUND CLEARED");
      return true;
    } catch (e) {
      toast.error("CLEAR FAILED", { description: e.response?.data?.detail || e.message });
      return false;
    }
  }, []);

  // Time-of-day trophies — fired once per page session.
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 1 && hour < 4) fireTrophy("night_owl");
    if (hour >= 5 && hour < 7) fireTrophy("early_bird");
  }, [fireTrophy]);

  // Konami code unlocker. Listens for the canonical sequence anywhere on
  // the page (capture-phase to bypass focused inputs). Fires the Code Breaker
  // trophy + a brief PHANTOM SIGNAL overlay for a wink of payoff.
  useEffect(() => {
    const seq = ["ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown", "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight", "b", "a"];
    let pos = 0;
    const onKey = (e) => {
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      pos = k === seq[pos] ? pos + 1 : (k === seq[0] ? 1 : 0);
      if (pos === seq.length) {
        pos = 0;
        fireTrophy("code_breaker");
        setShowPhantom(true);
        setTimeout(() => setShowPhantom(false), 4000);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fireTrophy]);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < SMALL_VIEWPORT_PX);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const handleMobileContinue = useCallback(() => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch (e) { /* noop */ }
    setMobileDismissed(true);
  }, []);

  // Persist volume across reloads — written to both the legacy `crt_volume`
  // key AND the unified viewer-prefs blob so the ViewerSettings panel and
  // the remote stay in sync.
  useEffect(() => {
    try { localStorage.setItem(VOLUME_STORAGE, String(volume)); } catch (e) { /* noop */ }
    try {
      const cur = getPrefs();
      if (cur.volume !== volume) patchPrefs({ volume });
    } catch (e) { /* noop */ }
  }, [volume]);

  // Mirror direction: ViewerSettings slider → App volume state.
  //
  // IMPORTANT: react ONLY when the preference changes. `volume` is the
  // canonical live player state; including it here causes a remote nudge to
  // race the still-old viewerPrefs snapshot and bounce the value backwards.
  useEffect(() => {
    if (viewerPrefs && typeof viewerPrefs.volume === "number") {
      setVolume((current) => {
        const next = normaliseVolume(viewerPrefs.volume, current);
        return current === next ? current : next;
      });
    }
  }, [viewerPrefs.volume]);

  // Persist scanline intensity across reloads
  useEffect(() => {
    try { localStorage.setItem(SCANLINE_STORAGE, String(scanlineLevel)); } catch (e) { /* noop */ }
  }, [scanlineLevel]);

  const osdChannelTimer = useRef(null);
  const osdVolumeTimer = useRef(null);
  const staticTimer = useRef(null);
  // Tracks the id of the channel the viewer is actively watching. We mirror
  // currentChannel.id into a ref so fetchChannels() can re-locate the same
  // logical channel after the lineup is rebuilt (e.g. when ★ FAVOURITES or
  // CONTINUE WATCHING virtual channels get inserted/removed at index 0).
  // Without this, channelIndex would point to whatever now sits at the same
  // numeric slot — silently tuning the user away from their channel.
  const tunedChannelIdRef = useRef(null);
  // Stores the channel id we tuned AWAY from. Used by the LAST button on
  // the remote (and the `L` keyboard shortcut) to bounce back to the
  // previous channel — same affordance as a real TV remote's PREV. Updated
  // by changeChannel / tuneToNumber / tuneToChannel only when leaving a
  // non-virtual channel; virtuals like ★ FAVOURITES aren't worth bouncing
  // to/from. Null at boot.
  const previousChannelIdRef = useRef(null);
  // Snapshot of the currently-dormant channel ids from the last
  // fetchChannels response. Used to detect a dormant→live transition
  // and pop a "📡 NAME IS NOW LIVE" toast when the owner flips the
  // switch (or when the poll picks up an owner-side change from
  // another tab). `initialised` gates the first fetch so we don't
  // toast on page load for every already-live channel.
  const dormantSnapshotRef = useRef({ initialised: false, ids: new Set(), meta: new Map() });
  // Tracks whether we've already picked a random starting tape for this
  // page load. Without this gate, the random pick would re-run on every
  // channel-list rebuild (e.g. ★ FAVOURITES insertion) which would yank
  // the viewer to a different episode mid-playback.
  const initialRandomizedRef = useRef(false);
  // Phase 2.3.62.3-r2 - monotonic request id prevents a slow /live response
  // from an old channel zap overwriting a newer tune.
  const liveTuneRequestRef = useRef(0);
  // Per-channel viewer cursor — `channelId → { tapeIdx }`. Each visitor has
  // their own independent position in each channel's playlist; we no longer
  // sync to a shared wall-clock broadcast schedule (multi-user mode: every
  // viewer watches what they like, when they like).
  //
  // Persisted to localStorage so closing the tab and re-opening tomorrow
  // resumes each channel exactly where this viewer left off.
  const CHANNEL_STATE_KEY = "crt_channel_state";
  const channelStateRef = useRef((() => {
    try {
      const raw = localStorage.getItem(CHANNEL_STATE_KEY);
      if (!raw) return new Map();
      const parsed = JSON.parse(raw);
      return new Map(Object.entries(parsed));
    } catch (e) { return new Map(); }
  })());
  // Tiny helper to flush the Map back to localStorage. Called any time the
  // cursor changes (changeChannel / tuneToChannel / handleVideoEnded).
  // Throttling is unnecessary — these events are rare and the payload is tiny.
  const persistChannelState = () => {
    try {
      const obj = Object.fromEntries(channelStateRef.current);
      localStorage.setItem(CHANNEL_STATE_KEY, JSON.stringify(obj));
    } catch (e) { /* quota / private mode — noop */ }
  };
  // Seconds to seek-into the next tape when the player spawns. Reset to 0
  // after a natural auto-advance (since the new tape genuinely starts at 0)
  // or an explicit guide pick. Kept around for compatibility with CRT.js'
  // resume-on-mount path; multi-user mode just leaves it at 0 since each
  // viewer's last-known position is owned by CRT.js / watchProgress.js, not
  // by a global broadcast clock.
  const [initialSeekSec, setInitialSeekSec] = useState(0);
  const [liveTuneActive, setLiveTuneActive] = useState(false);

  // Resume the tape index this viewer was on for `channelId`. First visit
  // to that channel → random pick. NO time-based fast-forward; each viewer
  // controls their own playhead.
  const getOrPickTapeIndex = (channelId, vids) => {
    if (!vids || vids.length === 0) return 0;
    const state = channelStateRef.current.get(channelId);
    if (state && typeof state.tapeIdx === "number") {
      return Math.min(state.tapeIdx, vids.length - 1);
    }
    const tapeIdx = Math.floor(Math.random() * vids.length);
    channelStateRef.current.set(channelId, { tapeIdx });
    persistChannelState();
    return tapeIdx;
  };

  // Phase 2.3.62.3-r2 - authoritative wall-clock live tune.
  // Real channels resolve their programme + offset from the backend broadcast
  // clock. Virtual/sub-channel lanes deliberately retain their existing local
  // behaviour until their separate scheduling semantics are designed.
  const tuneToLiveChannel = useCallback(async (channel, vids) => {
    if (!channel || !channel.id || channel.virtual || channel.isSubChannel) {
      return { ok: false, stale: false, reason: "non-linear-channel" };
    }
    if (!Array.isArray(vids) || vids.length === 0) {
      return { ok: false, stale: false, reason: "empty-playlist" };
    }

    const requestId = ++liveTuneRequestRef.current;
    try {
      window.__sltvBroadcastTuneStatus = {
        state: "RESOLVING",
        channelId: channel.id,
        requestId,
      };
    } catch (_) {}

    try {
      const res = await axios.get(
        `${API}/channels/${encodeURIComponent(channel.id)}/live`
      );

      if (requestId !== liveTuneRequestRef.current) {
        return { ok: false, stale: true, reason: "superseded" };
      }

      const live = res?.data || {};
      let idx = -1;

      if (live.video_id) {
        idx = vids.findIndex((v) => v && v.id === live.video_id);
      }
      if (
        idx < 0 &&
        Number.isInteger(live.video_index) &&
        live.video_index >= 0 &&
        live.video_index < vids.length
      ) {
        idx = live.video_index;
      }

      const offset = Number(live.offset_sec);
      if (idx < 0 || !Number.isFinite(offset) || offset < 0) {
        throw new Error("invalid live-position response");
      }

      setVideoIndex(idx);
      setInitialSeekSec(offset);
      setLiveTuneActive(true);

      try {
        window.__sltvBroadcastTuneStatus = {
          state: "LIVE",
          channelId: channel.id,
          videoId: vids[idx]?.id || live.video_id || null,
          videoIndex: idx,
          offsetSec: offset,
          serverTime: live.server_time || null,
          programmeStart: live.programme_start || null,
          programmeEnd: live.programme_end || null,
          requestId,
        };
      } catch (_) {}

      return { ok: true, stale: false };
    } catch (e) {
      if (requestId !== liveTuneRequestRef.current) {
        return { ok: false, stale: true, reason: "superseded" };
      }
      setLiveTuneActive(false);
      try {
        window.__sltvBroadcastTuneStatus = {
          state: "FAILED",
          channelId: channel.id,
          reason: e?.response?.data?.detail || e?.message || "live tune failed",
          requestId,
        };
      } catch (_) {}
      console.warn("[broadcast-clock] live tune failed", channel.id, e);
      return { ok: false, stale: false, reason: "request-failed" };
    }
  }, []);

  const currentChannel = channels[channelIndex];

  // Persist the viewer's currently-tuned (real) channel so the "REMEMBER
  // LAST CHANNEL" preference can resume them here on their next visit.
  // Virtual channels (★ CONTACT / FAVOURITES / CONTINUE WATCHING) are
  // intentionally NOT remembered — those are personal/utility channels,
  // not "the last show I was watching".
  useEffect(() => {
    if (!currentChannel || currentChannel.virtual || !currentChannel.id) return;
    try { localStorage.setItem("crt_last_channel_id", currentChannel.id); } catch (e) { /* noop */ }
  }, [currentChannel && currentChannel.id, currentChannel && currentChannel.virtual]);
  const currentVideos = currentChannel ? (videosByChannel[currentChannel.id] || []) : [];
  const currentVideo = currentVideos[videoIndex];

  // Phase 2.3.62.6-r4.1 - CRT power does not pause the station clock.
  // On a genuine OFF -> ON transition, rejoin the currently tuned linear
  // channel at the backend's authoritative programme + live offset.
  const powerLiveResyncPrevRef = useRef(power);
  const powerLiveResyncTargetRef = useRef({ channel: currentChannel, vids: currentVideos });
  powerLiveResyncTargetRef.current = { channel: currentChannel, vids: currentVideos };

  useEffect(() => {
    const wasPowered = powerLiveResyncPrevRef.current;
    powerLiveResyncPrevRef.current = power;

    if (wasPowered || !power) return undefined;

    const target = powerLiveResyncTargetRef.current || {};
    const channel = target.channel;
    const vids = Array.isArray(target.vids) ? target.vids : [];

    if (
      !channel ||
      !channel.id ||
      channel.virtual ||
      channel.isSubChannel ||
      vids.length === 0
    ) {
      return undefined;
    }

    let cancelled = false;

    try {
      window.__sltvPowerResyncStatus = {
        state: "RESOLVING",
        channelId: channel.id,
        at: new Date().toISOString(),
      };
    } catch (_) {}

    void (async () => {
      const result = await tuneToLiveChannel(channel, vids);
      if (cancelled) return;

      try {
        window.__sltvPowerResyncStatus = {
          state: result.ok
            ? "LIVE"
            : result.stale
              ? "SUPERSEDED"
              : "FAILED",
          channelId: channel.id,
          at: new Date().toISOString(),
          reason: result.reason || null,
        };
      } catch (_) {}
    })();

    return () => {
      cancelled = true;
    };
  }, [power, tuneToLiveChannel]);

  // Phase 2.3.59 — keep the browser tab synced with the live broadcast.
  // React state is authoritative here, so channel changes, manual programme
  // changes and automatic programme advances all update the title instantly.
  useEffect(() => {
    const fallback = "SCARTLEAD.TV · Cartoons, Movies & TV Shows";

    if (!currentChannel || currentChannel.virtual) {
      document.title = fallback;
      return;
    }

    const channelLabel =
      currentChannel.numberLabel ||
      (currentChannel.number != null ? String(currentChannel.number).padStart(2, "0") : "");

    const programmeTitle =
      currentVideo && currentVideo.title
        ? String(currentVideo.title).trim()
        : "";

    const channelName = currentChannel.name
      ? String(currentChannel.name).trim()
      : "";

    if (programmeTitle) {
      document.title = `${channelLabel ? `CH ${channelLabel} · ` : ""}${programmeTitle} · SCARTLEAD.TV`;
    } else if (channelName) {
      document.title = `${channelLabel ? `CH ${channelLabel} · ` : ""}${channelName} · SCARTLEAD.TV`;
    } else {
      document.title = fallback;
    }
  }, [currentChannel, currentVideo]);

  // Phase 2.3.69-r1.3 — a VIEW and WATCH TIME are separate facts.
  // One confirmed tape entry emits one deduplicated view; only completed
  // 30-second confirmed-playing intervals contribute watch time.
  useEffect(() => {
    const active = ownerChecked && !isOwner && power && !maintenance &&
      statsMediaPlaying && !remotePaused && !!currentVideo && !!currentChannel && !currentChannel.virtual;
    if (!active) return undefined;
    trackView(currentChannel.id, currentVideo.id);
    const iv = setInterval(() => trackWatch(currentChannel.id, currentVideo.id, 30), 30000);
    return () => clearInterval(iv);
  }, [ownerChecked, isOwner, power, maintenance, statsMediaPlaying, remotePaused,
      currentVideo && currentVideo.id, currentChannel && currentChannel.id,
      currentChannel && currentChannel.virtual]);


  // Phase 2.3.62.4-r1 — authoritative Guide/SELECT-peek snapshot.
  // The backend resolves ALL real channels at one shared UTC instant using
  // core.broadcast_clock.resolve_broadcast_position(), the same clock used by
  // player live tuning. No local channel cursor or tape-0 fallback is allowed.
  const [guideLiveSnapshot, setGuideLiveSnapshot] = useState([]);
  const [guideLiveServerTime, setGuideLiveServerTime] = useState(null);
  // Phase 2.3.62.4-r3 — authoritative real-time EPG schedule window.
  const [guideScheduleWindow, setGuideScheduleWindow] = useState(null);

  // Phase 2.3.62.6-r1 — App remains the sole owner of the authoritative Guide
  // window. Extended bounds live in a ref so the normal 30-second refresh
  // preserves the viewer's browsing range instead of snapping back to NOW.
  const GUIDE_WINDOW_DEFAULT_PAST_MINUTES = 30;
  const GUIDE_WINDOW_DEFAULT_FUTURE_MINUTES = 120;
  const GUIDE_WINDOW_MAX_PAST_MINUTES = 240;
  const GUIDE_WINDOW_MAX_FUTURE_MINUTES = 720;
  const guideWindowRequestRef = useRef({
    pastMinutes: GUIDE_WINDOW_DEFAULT_PAST_MINUTES,
    futureMinutes: GUIDE_WINDOW_DEFAULT_FUTURE_MINUTES,
  });

  const refreshGuideLiveSnapshot = useCallback(async (windowRequest = null) => {
    const currentRequest = guideWindowRequestRef.current || {};
    const requestedPast = Number.isFinite(windowRequest?.pastMinutes)
      ? windowRequest.pastMinutes
      : currentRequest.pastMinutes;
    const requestedFuture = Number.isFinite(windowRequest?.futureMinutes)
      ? windowRequest.futureMinutes
      : currentRequest.futureMinutes;
    const pastMinutes = Math.max(
      0,
      Math.min(GUIDE_WINDOW_MAX_PAST_MINUTES, Number(requestedPast) || GUIDE_WINDOW_DEFAULT_PAST_MINUTES)
    );
    const futureMinutes = Math.max(
      15,
      Math.min(GUIDE_WINDOW_MAX_FUTURE_MINUTES, Number(requestedFuture) || GUIDE_WINDOW_DEFAULT_FUTURE_MINUTES)
    );

    try {
      const [res, scheduleRes] = await Promise.all([
        axios.get(`${API}/channels/live-guide`),
        axios.get(`${API}/channels/live-guide-window`, {
          params: {
            past_minutes: pastMinutes,
            future_minutes: futureMinutes,
          },
        }),
      ]);
      const body = res?.data || {};
      const rows = Array.isArray(body.channels) ? body.channels : [];
      const scheduleBody = scheduleRes?.data || null;

      guideWindowRequestRef.current = { pastMinutes, futureMinutes };
      setGuideLiveSnapshot(rows);
      setGuideLiveServerTime(body.server_time || null);
      setGuideScheduleWindow(scheduleBody);

      try {
        window.__sltvGuideClockStatus = {
          state: "ACTIVE",
          serverTime: body.server_time || null,
          rows: rows.length,
          errors: Array.isArray(body.errors) ? body.errors : [],
          scheduleRows: Array.isArray(scheduleBody?.channels) ? scheduleBody.channels.length : 0,
          scheduleErrors: Array.isArray(scheduleBody?.errors) ? scheduleBody.errors : [],
          scheduleWindowStart: scheduleBody?.window_start || null,
          scheduleWindowEnd: scheduleBody?.window_end || null,
          requestedPastMinutes: pastMinutes,
          requestedFutureMinutes: futureMinutes,
          refreshedAt: new Date().toISOString(),
        };
      } catch (_) {}

      return { ok: true, schedule: scheduleBody, pastMinutes, futureMinutes };
    } catch (e) {
      try {
        window.__sltvGuideClockStatus = {
          state: "FAILED",
          reason: e?.response?.data?.detail || e?.message || "guide clock unavailable",
          requestedPastMinutes: pastMinutes,
          requestedFutureMinutes: futureMinutes,
          refreshedAt: new Date().toISOString(),
        };
      } catch (_) {}
      return {
        ok: false,
        reason: e?.response?.data?.detail || e?.message || "guide clock unavailable",
        pastMinutes,
        futureMinutes,
      };
    }
  }, []);

  const extendGuideScheduleWindow = useCallback(async (direction) => {
    const current = guideWindowRequestRef.current || {
      pastMinutes: GUIDE_WINDOW_DEFAULT_PAST_MINUTES,
      futureMinutes: GUIDE_WINDOW_DEFAULT_FUTURE_MINUTES,
    };
    const next = {
      pastMinutes: Number(current.pastMinutes) || GUIDE_WINDOW_DEFAULT_PAST_MINUTES,
      futureMinutes: Number(current.futureMinutes) || GUIDE_WINDOW_DEFAULT_FUTURE_MINUTES,
    };

    if (direction === "left") {
      next.pastMinutes = Math.min(GUIDE_WINDOW_MAX_PAST_MINUTES, next.pastMinutes + 60);
    } else if (direction === "right") {
      next.futureMinutes = Math.min(GUIDE_WINDOW_MAX_FUTURE_MINUTES, next.futureMinutes + 120);
    } else {
      return { expanded: false, reason: "invalid-direction" };
    }

    if (
      next.pastMinutes === current.pastMinutes &&
      next.futureMinutes === current.futureMinutes
    ) {
      return {
        expanded: false,
        limit: true,
        pastMinutes: current.pastMinutes,
        futureMinutes: current.futureMinutes,
      };
    }

    const result = await refreshGuideLiveSnapshot(next);
    return { ...result, expanded: !!result?.ok, direction };
  }, [refreshGuideLiveSnapshot]);

  useEffect(() => {
    refreshGuideLiveSnapshot();
    const timer = setInterval(refreshGuideLiveSnapshot, 30000);
    return () => clearInterval(timer);
  }, [refreshGuideLiveSnapshot]);

  // Refresh immediately when the Guide opens or the SELECT peek is invoked,
  // rather than waiting for the next 30-second background cadence.
  useEffect(() => {
    if (mode === "guide") {
      refreshGuideLiveSnapshot({
        pastMinutes: GUIDE_WINDOW_DEFAULT_PAST_MINUTES,
        futureMinutes: GUIDE_WINDOW_DEFAULT_FUTURE_MINUTES,
      });
    } else if (peekTick > 0) {
      refreshGuideLiveSnapshot();
    }
  }, [mode, peekTick, refreshGuideLiveSnapshot]);

  const epgPeek = useMemo(() => {
    const liveById = new Map(
      (guideLiveSnapshot || [])
        .filter((row) => row && row.id)
        .map((row) => [row.id, row])
    );

    return channels
      .filter((c) => !c.virtual)
      .map((c) => {
        const live = liveById.get(c.id);
        if (!live) return null;
        return {
          ...live,
          number: c.number,
          numberLabel: c.numberLabel || String(c.number).padStart(2, "0"),
          name: c.name,
          isCurrent: !!(currentChannel && currentChannel.id === c.id),
          guideServerTime: guideLiveServerTime,
        };
      })
      .filter(Boolean);
  }, [guideLiveSnapshot, guideLiveServerTime, channels, currentChannel]);

  // Keep the "what channel is the viewer tuned to" ref in sync with state.
  // fetchChannels reads this AFTER rebuilding the lineup to re-anchor the
  // index by id (see comment on tunedChannelIdRef).
  useEffect(() => {
    if (currentChannel && currentChannel.id) {
      tunedChannelIdRef.current = currentChannel.id;
    }
  }, [currentChannel]);

  const fetchChannels = useCallback(async () => {
    const [res, contactCfgRes, catRes, virtualMetaRes] = await Promise.all([
      // Owner header lets the /channels endpoint include DORMANT (seasonal
      // preload) channels so the management panel still lists them. For
      // non-owner viewers `authHeaders()` returns `{}` and the endpoint
      // hides dormant channels — pure server-side filter, no chance of a
      // dormant channel leaking into the guide via a client bug.
      axios.get(`${API}/channels`, { headers: authHeaders() }),
      // Owner-editable copy for the ★ CONTACT virtual channel. Failing
      // silently keeps the lineup working with default copy on an outage.
      axios.get(`${API}/site/contact-channel`).catch(() => ({ data: {} })),
      // Sky Q-style rail categories. Failing silently leaves the guide
      // with a single "UNCATEGORISED" rail rather than crashing it.
      axios.get(`${API}/categories`).catch(() => ({ data: [] })),
      // Owner-uploaded artwork for the virtual channels (CONTACT /
      // FAVOURITES). Returns `{kind: {thumbnail_path: str|null}}`. A
      // failure here just means the placeholder card text shows instead
      // of custom artwork — no need to crash the lineup over it.
      axios.get(`${API}/site/virtual-channels`).catch(() => ({ data: {} })),
    ]);
    const contactCfg = contactCfgRes.data || {};
    const virtualMeta = virtualMetaRes.data || {};
    setCategories(catRes.data || []);

    // Detect dormant → live transitions so the owner (and anyone else
    // watching the lineup live) gets a subtle "📡 NAME IS NOW LIVE"
    // toast the instant a preloaded seasonal channel is flipped
    // on-air. Comparison uses id-level Sets; skipped on first mount
    // so page-load doesn't fire a toast for every already-live
    // channel.
    const rawChannels = res.data || [];
    const nextDormantIds = new Set(rawChannels.filter((c) => c.is_dormant).map((c) => c.id));
    const nextMeta = new Map(rawChannels.map((c) => [c.id, { name: c.name, number: c.number }]));
    if (dormantSnapshotRef.current.initialised) {
      const prevIds = dormantSnapshotRef.current.ids;
      const prevMeta = dormantSnapshotRef.current.meta;
      for (const c of rawChannels) {
        if (prevIds.has(c.id) && !c.is_dormant) {
          const meta = prevMeta.get(c.id) || { name: c.name, number: c.number };
          toast.success(`📡 ${meta.name} IS NOW LIVE`, {
            description: `Channel ${String(meta.number).padStart(2, "0")} just went on-air.`,
            duration: 6000,
          });
        }
      }
    }
    dormantSnapshotRef.current = { initialised: true, ids: nextDormantIds, meta: nextMeta };

    const map = {};
    await Promise.all(
      res.data.map(async (c) => {
        const v = await axios.get(`${API}/channels/${c.id}/videos`);
        map[c.id] = v.data;
      })
    );

    // (Removed 2026-02-25: "CONTINUE WATCHING" virtual channel — owner
    // request. Individual tape resume still works via the watch-progress
    // mechanism; we just don't aggregate them into a top-of-lineup row.)
    const allVideos = Object.values(map).flat();
    const byId = new Map(allVideos.map((v) => [v.id, v]));

    // Off-air filter — channels with zero finalised videos are hidden from
    // viewers until at least one tape is ready to broadcast (matches the
    // real-TV model: a station "goes live" when it has something to
    // broadcast). Owners always see every channel they own so they can
    // upload to in-progress channels without losing visibility of them in
    // the lineup. Virtuals (★ FAVOURITES) are added later and never go
    // through this filter.
    const liveChannels = isOwner
      ? res.data
      : res.data.filter((c) => (map[c.id] || []).length > 0);
    const channelList = [...liveChannels];

    // "★ FAVOURITES" virtual channel — collects every tape the user has
    // starred (in the EPISODES list of the guide), across all real channels.
    // Only rendered when there's at least one favourite, mirroring the
    // CONTINUE WATCHING behaviour above.
    const favIds = listFavourites();
    const favVideos = favIds.map((id) => byId.get(id)).filter(Boolean);
    if (favVideos.length > 0) {
      channelList.unshift({
        id: FAV_ID,
        number: 0,
        numberLabel: "★",
        name: "FAVOURITES",
        description: `${favVideos.length} starred tape${favVideos.length === 1 ? "" : "s"} from across the lineup`,
        // Owner-uploaded artwork (when set) overrides the placeholder
        // ★ FAVS card in the guide. A non-null string here is enough —
        // the actual URL is built in ChannelThumb from the kind.
        thumbnail_path: (virtualMeta.favourites && virtualMeta.favourites.thumbnail_path) || null,
        // Linked-URL variant of the above — populates from the same
        // /site/virtual-channels payload so the Guide's LINK URL
        // popover pre-fills correctly and every art-render site sees
        // both signals.
        thumbnail_url: (virtualMeta.favourites && virtualMeta.favourites.thumbnail_url) || null,
        // Guide-poster URL for the mini-preview tile in the Channel
        // Guide. Same shape as `thumbnail_url` — link-only for virtual
        // channels (no uploaded path variant yet).
        guide_poster_url: (virtualMeta.favourites && virtualMeta.favourites.guide_poster_url) || null,
        logo_path: null,
        virtual: true,
        kind: "favourites",
      });
      map[FAV_ID] = favVideos;
    }

    // ★ CONTACT virtual channel — pinned at the TOP of the lineup
    // (above ★ FAVOURITES) so reaching the station operator is always
    // one channel-up away. Tuning to it swaps the video player for an
    // in-CRT contact form (see render below). The channel "playlist" is
    // intentionally empty; the form handles its own state. Name +
    // description are owner-editable via /api/site/contact-channel.
    channelList.unshift({
      id: CONTACT_ID,
      number: 0,
      numberLabel: "✉",
      name: contactCfg.name || "CONTACT",
      description: contactCfg.description || "Reach the station operator. Messages go straight to the owner's inbox.",
      // Owner-uploaded artwork (when set) overrides the placeholder
      // ✉ CONTACT card in the guide. A non-null string is enough — the
      // actual URL is built in ChannelThumb from the kind.
      thumbnail_path: (virtualMeta.contact && virtualMeta.contact.thumbnail_path) || null,
      // Linked-URL variant — same purpose as the path variant just
      // above, populated from the same /site/virtual-channels payload.
      thumbnail_url: (virtualMeta.contact && virtualMeta.contact.thumbnail_url) || null,
      // Guide-poster URL for the mini-preview tile in the Channel
      // Guide (link-only for virtual channels).
      guide_poster_url: (virtualMeta.contact && virtualMeta.contact.guide_poster_url) || null,
      logo_path: null,
      virtual: true,
      kind: "contact",
    });
    map[CONTACT_ID] = [];

    // Sky-Q sub-channel expansion — inject "5.1 / 5.2 / 5.3" style
    // entries for every real channel that carries ≥ 2 distinct
    // show_titles across its tapes. Zero backend schema changes at
    // the channel level; sub-channels are derived + synthesized here
    // so the guide, browse, and CRT playback all see them as normal
    // channels with their own filtered tape list. Owner-only paths
    // filter them out via `isSubChannel: true`.
    const expanded = expandChannelsWithShows(channelList, map);
    setChannels(expanded.channels);
    setVideosByChannel(expanded.videosByChannel);
    setLineupReady(true);

    // Re-anchor channelIndex to the SAME logical channel id the viewer was
    // tuned to before the rebuild. This prevents the lineup-shuffle bug
    // where prepending ★ FAVOURITES or CONTINUE WATCHING silently shifts
    // the user from (say) channel 1 → FAVOURITES because both share index 0.
    const prevId = tunedChannelIdRef.current;
    if (prevId) {
      const newIdx = channelList.findIndex((c) => c.id === prevId);
      if (newIdx >= 0) {
        setChannelIndex(newIdx);
      }
      // newIdx === -1 ⇒ the channel the viewer was on no longer exists
      // (e.g. they were on FAVOURITES and just unstarred the last tape).
      // Fall through and let channelIndex stay where it is; the bounded
      // currentChannel = channels[channelIndex] will pick the channel now
      // sitting at that index, which is the expected "auto re-tune" behaviour.
    } else {
      // Brand-new mount: always tune to a random real (non-virtual)
      // channel. The previous four-mode starting-channel pref (first /
      // last / fixed / surprise) was removed — the front page now
      // always feels like flipping-on a real TV, landing somewhere new
      // every visit. Falls back to `channelIndex = 0` when there are
      // no real channels yet (fresh install) so the lineup effect
      // doesn't leave the viewer on a stale placeholder.
      const realIdx = channelList
        .map((c, i) => ({ c, i }))
        .filter(({ c }) => !c.virtual)
        .map(({ i }) => i);
      if (realIdx.length) {
        // Phase 2.3.61h.4-r6 — /programmes owns a deterministic startup.
      const neutralProgrammes =
        window.__SCARTLEAD_DEEPLINK__?.neutral_entry === true &&
        window.__SCARTLEAD_DEEPLINK__?.canonical_path === "/programmes" &&
        window.location.pathname === "/programmes";

      const pick = neutralProgrammes
        ? realIdx[0]
        : realIdx[Math.floor(Math.random() * realIdx.length)];
        setChannelIndex(pick);
      }
    }
  }, [isOwner]);

  // Keep the categories-updated event handler's reference in sync with
  // the latest fetchChannels. useCallback recreates the function each
  // render; we want the listener to always invoke the freshest closure.
  useEffect(() => {
    fetchChannelsRef.current = fetchChannels;
  }, [fetchChannels]);

  // phase-2.3.81a-r3-series-info — additive viewer-opened series experience.
  const [seriesInfoOpen, setSeriesInfoOpen] = useState(false);
  // Phase 2.3.81b — versioned feature discovery for established viewers.
  const WHATS_NEW_SERIES_INFO_KEY = "crt_whats_new_series_info_2_3_81";
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);

  // Keep the series shortcut isolated from the existing global keyboard handler
  // so its dependencies are explicit and React's hook lint stays clean.
  useEffect(() => {
    const handleSeriesInfoShortcut = (event) => {
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target?.isContentEditable) return;
      if ((event.key === "i" || event.key === "I") && mode === "tv" && currentVideo?.show_title) {
        event.preventDefault();
        setSeriesInfoOpen(true);
      }
    };
    window.addEventListener("keydown", handleSeriesInfoShortcut);
    return () => window.removeEventListener("keydown", handleSeriesInfoShortcut);
  }, [mode, currentVideo?.show_title]);

  // Owner bootstrap: auto-claim on first ever visit; verify stored token otherwise
  useEffect(() => {
    (async () => {
      try {
        const status = await axios.get(`${API}/owner/status`);
        if (!status.data.claimed) {
          // First visit ever — this device becomes owner
          const res = await axios.post(`${API}/owner/claim`);
          localStorage.setItem(OWNER_KEY_STORAGE, res.data.token);
          setIsOwner(true);
          toast.success("OWNER KEY GENERATED", {
            description: "Saved to this device. Open MENU > OWNER to back it up.",
            duration: 8000,
          });
        } else {
          const stored = getStoredToken();
          if (stored) {
            const v = await axios.post(`${API}/owner/verify`, { token: stored });
            setIsOwner(!!v.data.valid);
          } else {
            setIsOwner(false);
          }
        }
      } catch (e) {
        setIsOwner(false);
      } finally {
        setOwnerChecked(true);
      }
    })();
  }, []);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  // Rebuild channels whenever the user stars/unstars a tape so the
  // ★ FAVOURITES virtual channel appears/disappears/repopulates live.
  useEffect(() => {
    const onChange = () => { fetchChannels(); };
    window.addEventListener("crt:favourites-changed", onChange);
    return () => window.removeEventListener("crt:favourites-changed", onChange);
  }, [fetchChannels]);

  // One-shot random starting tape on the boot channel. After this, the
  // per-channel cursor takes over (see `getOrPickTapeIndex`). Skipping on
  // refetches avoids yanking the viewer mid-watch when ★ FAVOURITES gets
  // inserted etc.
  useEffect(() => {
    if (initialRandomizedRef.current) return;
    if (channels.length === 0) return;
    const ch = channels[channelIndex];
    if (!ch) return;
    const vids = videosByChannel[ch.id] || [];
    if (vids.length === 0) return; // wait until videos arrive for this channel
    initialRandomizedRef.current = true;

    // URL deep-link override.
    // New format: `/ch/<number>` or `/ch/<number>/t/<tape-id>`. Old format
    // `?ch=N&t=M` is still parsed as a fallback so existing copy-pasted
    // links keep working.
    try {
      let wantedNumber = null;
      let wantedTapeId = null;
      let wantedTapeIndex = null; // legacy 1-based index path

      const friendly = window.__SCARTLEAD_DEEPLINK__;
      if (friendly && friendly.channel_number != null) {
        wantedNumber = parseInt(friendly.channel_number, 10);
        if (friendly.tape_id) wantedTapeId = String(friendly.tape_id);
      } else {
        const m = window.location.pathname.match(/^\/ch\/(\d+)(?:\/t\/([^/?#]+))?/i);
        if (m) {
          wantedNumber = parseInt(m[1], 10);
          if (m[2]) wantedTapeId = decodeURIComponent(m[2]);
        } else {
          const params = new URLSearchParams(window.location.search);
          const chParam = params.get("ch");
          const tParam = params.get("t");
          if (chParam) {
            wantedNumber = parseInt(chParam, 10);
            if (tParam) wantedTapeIndex = Math.max(0, parseInt(tParam, 10) - 1);
          }
        }
      }

      // Phase 2.3.61h.4-r3 — /programmes is a deterministic neutral entry.
      // It NEVER calls getOrPickTapeIndex. Resume the exact last public
      // channel/programme when possible; otherwise use the first public
      // channel and its first programme.
      const neutralEntry = window.__SCARTLEAD_DEEPLINK__;
      if (
        neutralEntry?.neutral_entry === true &&
        neutralEntry?.canonical_path === "/programmes" &&
        window.location.pathname === "/programmes"
      ) {
        let targetIdx = -1;
        let savedChannelId = null;
        let savedVideoId = null;
        try {
          savedChannelId = localStorage.getItem("crt_last_channel_id");
          savedVideoId = localStorage.getItem("crt_last_video_id");
        } catch (e) {}

        if (savedChannelId) {
          targetIdx = channels.findIndex((c) => c.id === savedChannelId && !c.virtual);
        }
        if (targetIdx < 0) {
          targetIdx = channels.findIndex((c) => !c.virtual);
        }

        if (targetIdx >= 0) {
          const targetCh = channels[targetIdx];
          const targetVids = videosByChannel[targetCh.id] || [];
          setChannelIndex(targetIdx);

          let tapeIdx = 0;
          if (savedVideoId && targetVids.length > 0) {
            const savedIdx = targetVids.findIndex((v) => v.id === savedVideoId);
            if (savedIdx >= 0) tapeIdx = savedIdx;
          }
          if (targetVids.length > 0) {
            channelStateRef.current.set(targetCh.id, { tapeIdx });
            persistChannelState();
          }
          setVideoIndex(tapeIdx);
          setInitialSeekSec(0);
        }
        return;
      }

      if (wantedNumber != null && !Number.isNaN(wantedNumber)) {
        const targetIdx = channels.findIndex((c) => c.number === wantedNumber);
        if (targetIdx >= 0) {
          const targetCh = channels[targetIdx];
          const targetVids = videosByChannel[targetCh.id] || [];
          // Tune even if the channel has zero tapes — the CRT will show its
          // empty/NO TAPES slate, but the URL is still honoured (otherwise
          // an empty target silently bounces the viewer back to Ch 1).
          setChannelIndex(targetIdx);
          let tapeIdx = 0;
          const explicitTape = !!wantedTapeId || wantedTapeIndex != null;
          if (targetVids.length > 0 && explicitTape) {
            if (wantedTapeId) {
              const byId = targetVids.findIndex((v) => v.id === wantedTapeId);
              tapeIdx = byId >= 0 ? byId : 0;
            } else {
              tapeIdx = Math.min(wantedTapeIndex, targetVids.length - 1);
            }
            channelStateRef.current.set(targetCh.id, { tapeIdx });
            persistChannelState();
            setVideoIndex(tapeIdx);
            setInitialSeekSec(0);
            setLiveTuneActive(false);
          } else if (targetVids.length > 0) {
            void (async () => {
              const result = await tuneToLiveChannel(targetCh, targetVids);
              if (!result.ok && !result.stale) {
                const fallback = getOrPickTapeIndex(targetCh.id, targetVids);
                setVideoIndex(fallback);
                setInitialSeekSec(0);
                setLiveTuneActive(false);
              }
            })();
          } else {
            setVideoIndex(0);
            setInitialSeekSec(0);
            setLiveTuneActive(false);
          }
        }
        // Don't strip the URL — leave `/ch/N[/t/id]` in the address bar so
        // copy-pasting from there gives the same link. Query-string legacy
        // form still gets cleaned up below.
        if (window.location.search) {
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, "", cleanUrl);
        }
        return; // skip the random pick below
      }

      // Bare URL `/` (no /ch/, no ?ch=) → resume the viewer's last channel
      // from localStorage if we have one. New visitors with no saved state
      // fall through to the random-pick below, which lands them on the
      // first non-virtual channel.
      try {
        const lastId = localStorage.getItem("crt_last_channel_id");
        if (lastId) {
          const lastIdx = channels.findIndex((c) => c.id === lastId && !c.virtual);
          if (lastIdx >= 0) {
            const lastCh = channels[lastIdx];
            const lastVids = videosByChannel[lastCh.id] || [];
            setChannelIndex(lastIdx);
            if (lastVids.length > 0) {
              void (async () => {
                const result = await tuneToLiveChannel(lastCh, lastVids);
                if (!result.ok && !result.stale) {
                  const fallback = getOrPickTapeIndex(lastCh.id, lastVids);
                  setVideoIndex(fallback);
                  setInitialSeekSec(0);
                  setLiveTuneActive(false);
                }
              })();
            } else {
              setVideoIndex(0);
              setInitialSeekSec(0);
              setLiveTuneActive(false);
            }
            return; // skip the random pick below
          }
        }
      } catch (e) { /* localStorage unavailable — fall through */ }
    } catch (e) { /* URL parsing fail — fall through to random */ }

    void (async () => {
      const result = await tuneToLiveChannel(ch, vids);
      if (!result.ok && !result.stale) {
        const fallback = getOrPickTapeIndex(ch.id, vids);
        setVideoIndex(fallback);
        setInitialSeekSec(0);
        setLiveTuneActive(false);
      }
    })();
  }, [channels, channelIndex, videosByChannel, tuneToLiveChannel]);

  const triggerChannelOSD = useCallback(() => {
    setOsdChannel(true);
    if (osdChannelTimer.current) clearTimeout(osdChannelTimer.current);
    osdChannelTimer.current = setTimeout(() => setOsdChannel(false), 2500);
  }, []);

  const triggerVolumeOSD = useCallback(() => {
    setOsdVolume(true);
    if (osdVolumeTimer.current) clearTimeout(osdVolumeTimer.current);
    osdVolumeTimer.current = setTimeout(() => setOsdVolume(false), 1500);
  }, []);

  const triggerStatic = useCallback(() => {
    setIsStatic(true);
    if (staticTimer.current) clearTimeout(staticTimer.current);
    staticTimer.current = setTimeout(() => setIsStatic(false), 450);
  }, []);

  const changeChannel = useCallback((dir) => {
    if (!power || channels.length === 0) return;
    triggerStatic();
    const newIdx = (channelIndex + dir + channels.length) % channels.length;
    if (newIdx === channelIndex) return;
    // Remember the channel we're leaving so LAST can swap back. Virtuals
    // (★ FAVOURITES) are skipped — LAST is a "two real channels you're
    // bouncing between" affordance.
    const prevCh = channels[channelIndex];
    if (prevCh && !prevCh.virtual) previousChannelIdRef.current = prevCh.id;
    const newCh = channels[newIdx];
    const newVids = newCh ? (videosByChannel[newCh.id] || []) : [];
    setChannelIndex(newIdx);
    if (newCh && !newCh.virtual && !newCh.isSubChannel && newVids.length > 0) {
      void (async () => {
        const result = await tuneToLiveChannel(newCh, newVids);
        if (!result.ok && !result.stale) {
          const fallback = getOrPickTapeIndex(newCh.id, newVids);
          setVideoIndex(fallback);
          setInitialSeekSec(0);
          setLiveTuneActive(false);
        }
      })();
    } else if (newCh) {
      const tapeIdx = getOrPickTapeIndex(newCh.id, newVids);
      setVideoIndex(tapeIdx);
      setInitialSeekSec(0);
      setLiveTuneActive(false);
    } else {
      setVideoIndex(0);
      setInitialSeekSec(0);
      setLiveTuneActive(false);
    }
    triggerChannelOSD();
    // ── Trophies ─────────────────────────────────────────────────────
    channelHopsRef.current += 1;
    if (channelHopsRef.current >= 20) fireTrophy("channel_surfer");
    if (newCh && !newCh.virtual && !newCh.isSubChannel) {
      visitedChannelsRef.current.add(newCh.id);
      const realCount = channels.filter((c) => !c.virtual && !c.isSubChannel).length;
      if (realCount > 0 && visitedChannelsRef.current.size >= realCount) {
        fireTrophy("completionist");
      }
    }
  }, [power, channels, channelIndex, videosByChannel, triggerStatic, triggerChannelOSD, fireTrophy, tuneToLiveChannel]);

  // Quick-tune by channel NUMBER (vs id) — used by the digit-buffer hook
  // for keyboard digits + remote digit-pad presses. Mirrors the tuning side
  // of `changeChannel` (static glitch + OSD + tape resume) but skips the
  // dir-arithmetic; we look up the channel by its public `number` field.
  // Virtual channels (★ FAVOURITES) are excluded because they have no
  // dial-able number on a real TV. No-op on power-off or unknown numbers
  // so a stray "9" on a 1-3 cabinet feels like a missed remote press
  // rather than a hard error.
  const tuneToNumber = useCallback((num) => {
    if (!power || channels.length === 0) return;
    // Digit-dialing always lands on the parent channel — sub-channels
    // (e.g. 5.1) share the parent's integer number, but a keypad "5"
    // should never surprise the viewer into a filtered show feed.
    // Sub-navigation happens via CH+/CH− once they're on the parent.
    const idx = channels.findIndex((c) => !c.virtual && !c.isSubChannel && c.number === num);
    if (idx < 0) return;
    if (idx === channelIndex) {
      // Re-flash the OSD when re-selecting the same channel — gives the
      // viewer feedback that the digit was received.
      triggerChannelOSD();
      return;
    }
    triggerStatic();
    const prevCh = channels[channelIndex];
    if (prevCh && !prevCh.virtual) previousChannelIdRef.current = prevCh.id;
    const newCh = channels[idx];
    const newVids = videosByChannel[newCh.id] || [];
    setChannelIndex(idx);
    if (newVids.length > 0) {
      void (async () => {
        const result = await tuneToLiveChannel(newCh, newVids);
        if (!result.ok && !result.stale) {
          const fallback = getOrPickTapeIndex(newCh.id, newVids);
          setVideoIndex(fallback);
          setInitialSeekSec(0);
          setLiveTuneActive(false);
        }
      })();
    } else {
      setVideoIndex(0);
      setInitialSeekSec(0);
      setLiveTuneActive(false);
    }
    triggerChannelOSD();
    channelHopsRef.current += 1;
    if (channelHopsRef.current >= 20) fireTrophy("channel_surfer");
    visitedChannelsRef.current.add(newCh.id);
    const realCount = channels.filter((c) => !c.virtual && !c.isSubChannel).length;
    if (realCount > 0 && visitedChannelsRef.current.size >= realCount) {
      fireTrophy("completionist");
    }
  }, [power, channels, channelIndex, videosByChannel, triggerStatic, triggerChannelOSD, fireTrophy, tuneToLiveChannel]);

  // Digit buffer — the set of dial-able numbers is pre-computed from the
  // current channel list (excluding virtuals AND sub-channels; the sub's
  // parent covers its number and keypad tuning should never surprise
  // the viewer into a filtered show feed). CH+/CH− steps into subs.
  const availableChannelNumbers = useMemo(
    () => channels.filter((c) => !c.virtual && !c.isSubChannel).map((c) => c.number),
    [channels],
  );
  const { buffer: digitBuffer, pushDigit, reset: resetDigitBuffer } = useChannelDigitBuffer({
    availableNumbers: availableChannelNumbers,
    onCommit: tuneToNumber,
    enabled: power,
  });
  // Clear any half-typed digit when the TV powers off — otherwise the
  // buffer would still be "1_" when the viewer turns the TV back on.
  useEffect(() => { if (!power) resetDigitBuffer(); }, [power, resetDigitBuffer]);

  const changeVolume = useCallback((dir) => {
    if (!power) return;
    setVolume((v) => {
      const next = Math.max(0, Math.min(MAX_VOLUME, v + dir));
      if (next === MAX_VOLUME && dir > 0) fireTrophy("loud_and_proud");
      return next;
    });
    triggerVolumeOSD();
  }, [power, triggerVolumeOSD, fireTrophy]);

  // Physical remote volume control.
  // Uses the same volume state/persistence as every other control, but owns
  // its visual feedback independently so NOW BROADCASTING is never touched.
  const changeRemoteVolume = useCallback((dir) => {
    if (!power) return;

    setVolume((v) => {
      const next = Math.max(0, Math.min(MAX_VOLUME, v + dir));
      if (next === MAX_VOLUME && dir > 0) fireTrophy("loud_and_proud");

      setRemoteVolumeOsd(next);

      if (remoteVolumeOsdTimer.current) {
        clearTimeout(remoteVolumeOsdTimer.current);
      }
      remoteVolumeOsdTimer.current = setTimeout(() => {
        setRemoteVolumeOsd(null);
        remoteVolumeOsdTimer.current = null;
      }, 1800);

      return next;
    });
  }, [power, fireTrophy]);

  useEffect(() => () => {
    if (remoteVolumeOsdTimer.current) clearTimeout(remoteVolumeOsdTimer.current);
  }, []);

  const togglePower = useCallback(() => {
    setPower((prev) => {
      const next = !prev;
      // Power-on now stays in TV mode. The viewer opens the Guide when ready
      // via GUIDE, G, or / (search focus) instead of the Guide auto-loading.
      setMode("tv");
      return next;
    });
  }, []);

  // Poll site maintenance status — every visitor (incl. anonymous viewers).
  // 15s feels live enough without hammering Mongo, and we also hit it once on
  // mount so anon viewers see the overlay immediately.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/site/status`);
        if (cancelled) return;
        setMaintenance(!!r.data.maintenance);
        setMaintenanceMsg(r.data.message || "");
        setMaintenanceReturnsAt(r.data.returns_at || null);
        setMaintenanceChecked(true);
      } catch (e) { /* keep last known value */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Fetch the announcement notice once on mount AND whenever maintenance
  // status changes (so toggling maintenance off doesn't strand viewers
  // without a recent notice). No polling — owner re-publishes are reflected
  // on next page load, which is the explicit product behaviour.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/site/notice`);
        if (cancelled) return;
        setNotice(r.data && r.data.enabled ? r.data : null);
      } catch (e) { /* keep last known */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleAcknowledgeNotice = useCallback(() => {
    if (notice && notice.notice_id) {
      ackNotice(notice.notice_id);
      setNoticeAckedId(notice.notice_id);
    }
  }, [notice]);

  // Fetch mobile-warning copy on mount. Same fire-and-forget shape as the
  // notice fetch — no polling, owner edits take effect on next page load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/site/mobile-warning`);
        if (cancelled) return;
        setMobileWarning(r.data || { title: "", body: "" });
      } catch (e) { /* defaults baked into MobileWarning */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch the canonical updates payload on mount. update_id comes from the
  // server's Git-backed release feed (+ Station Bulletin timestamp), so the
  // remote can illuminate only when this browser has not opened that state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/site/updates`);
        if (cancelled) return;
        const data = r.data || { notes: "", update_id: "", features: [], recent_videos: [] };
        setUpdatesData(data);
        if (data.update_id) {
          let seen = "";
          try { seen = localStorage.getItem("sltv_updates_seen_id_v1") || ""; } catch (e) { /* private mode */ }
          setUpdatesAvailable(seen !== data.update_id);
        }
      } catch (e) { /* fall through */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch the editable viewer-tips copy on mount. Drives the unified hint
  // card inside the CRT (combined sound + fullscreen prompts) and pre-fills
  // the OwnerBar editor.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/site/viewer-tips`);
        if (cancelled) return;
        setViewerTips(r.data || viewerTips);
      } catch (e) { /* defaults baked into useState above */ }
    })();
    return () => { cancelled = true; };
  // viewerTips intentionally left out — fallback should be the initial
  // useState defaults, not the latest fetched state.
  }, []);

  const handleSaveViewerTips = useCallback(async (payload) => {
    try {
      const r = await axios.post(
        `${API}/site/viewer-tips`,
        payload,
        { headers: authHeaders() }
      );
      setViewerTips(r.data);
      toast.success("VIEWER TIPS SAVED");
      return true;
    } catch (e) {
      toast.error("SAVE FAILED", { description: e.response?.data?.detail || e.message });
      return false;
    }
  }, []);

  // Fetch the owner-editable WELCOME BACK subtitle on mount. Used by the
  // bumper inside the CRT and pre-fills the OwnerBar editor panel.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/site/welcome-message`);
        if (cancelled) return;
        setWelcomeMessage(r.data || { subtitle: "" });
      } catch (e) { /* fallback to default useState */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSaveWelcomeMessage = useCallback(async (payload) => {
    try {
      const r = await axios.post(
        `${API}/site/welcome-message`,
        payload,
        { headers: authHeaders() }
      );
      setWelcomeMessage(r.data);
      toast.success("WELCOME MESSAGE SAVED");
      return true;
    } catch (e) {
      toast.error("SAVE FAILED", { description: e.response?.data?.detail || e.message });
      return false;
    }
  }, []);

  const handleSavePledgeDrive = useCallback(async (payload) => {
    try {
      const r = await axios.post(
        `${API}/site/pledge-drive`,
        payload,
        { headers: authHeaders() }
      );
      setPledgeDrive(r.data);
      toast.success("PLEDGE DRIVE SAVED");
      return true;
    } catch (e) {
      toast.error("SAVE FAILED", { description: e.response?.data?.detail || e.message });
      return false;
    }
  }, []);

  const handleSaveMobileWarning = useCallback(async (payload) => {
    try {
      const r = await axios.post(
        `${API}/site/mobile-warning`,
        payload,
        { headers: authHeaders() }
      );
      setMobileWarning(r.data || { title: "", body: "" });
      toast.success("MOBILE WARNING SAVED");
      return true;
    } catch (e) {
      toast.error("SAVE FAILED", { description: e.response?.data?.detail || e.message });
      return false;
    }
  }, []);

  // UPDATES button (remote) → fetch latest updates payload then enter
  // the in-CRT "updates" mode so the CHECKING-FOR-UPDATES boot runs
  // every time, displayed *inside* the CRT screen (not as a full-page
  // overlay). We refetch on every press so the recent-shows list is
  // always live. Owner-initiated: also dismisses the guide / upload
  // panels so the viewer's eyes go straight to the boot sequence.
  const handleOpenUpdates = useCallback(async () => {
    let data = updatesData;
    try {
      const r = await axios.get(`${API}/site/updates`);
      data = r.data || { notes: "", update_id: "", features: [], recent_videos: [] };
      setUpdatesData(data);
    } catch (e) { /* still open the panel even if fetch failed */ }

    // Opening What's New acknowledges the exact canonical feed state.
    if (data?.update_id) {
      try { localStorage.setItem("sltv_updates_seen_id_v1", data.update_id); } catch (e) { /* private mode */ }
      setUpdatesAvailable(false);
    }
    setMode("updates");
  }, [updatesData]);

  const handleSaveUpdatesNotes = useCallback(async (notes) => {
    try {
      const r = await axios.post(
        `${API}/site/updates`,
        { notes },
        { headers: authHeaders() }
      );
      const data = r.data || { notes: "", update_id: "", features: [], recent_videos: [] };
      setUpdatesData(data);
      // Owner editing the bulletin creates a new feed state. Keep it visibly
      // unread until the Updates screen itself is opened.
      if (data.update_id) setUpdatesAvailable(true);
      toast.success("RELEASE NOTES SAVED");
      return true;
    } catch (e) {
      toast.error("SAVE FAILED", { description: e.response?.data?.detail || e.message });
      return false;
    }
  }, []);

  const handleSaveViewerUpdates = useCallback(async (features) => {
    try {
      const r = await axios.post(`${API}/site/updates/features`, { features }, { headers: authHeaders() });
      const data = r.data || { notes: "", update_id: "", features: [], recent_videos: [] };
      setUpdatesData(data);
      if (data.update_id) setUpdatesAvailable(true);
      toast.success("VIEWER UPDATES PUBLISHED");
      return true;
    } catch (e) {
      toast.error("PUBLISH FAILED", { description: e.response?.data?.detail || e.message });
      return false;
    }
  }, []);


  const handlePublishNotice = useCallback(async (payload) => {
    try {
      const r = await axios.post(
        `${API}/site/notice`,
        payload,
        { headers: authHeaders() }
      );
      setNotice(r.data && r.data.enabled ? r.data : null);
      // The owner shouldn't be re-blocked by their own notice; auto-ack it.
      if (r.data && r.data.notice_id) {
        ackNotice(r.data.notice_id);
        setNoticeAckedId(r.data.notice_id);
      }
      toast.success(r.data && r.data.enabled ? "NOTICE PUBLISHED — viewers will see it on next load" : "NOTICE STOPPED");
      return true;
    } catch (e) {
      toast.error("NOTICE SAVE FAILED", { description: e.response?.data?.detail || e.message });
      return false;
    }
  }, []);

  const handleToggleMaintenance = useCallback(async (payload) => {
    // payload may be { enabled, message, returns_at } (from the panel) or
    // undefined (legacy direct click). Fall back to a plain toggle.
    const next = payload && typeof payload.enabled === "boolean"
      ? payload.enabled
      : !maintenance;
    const message = payload && typeof payload.message === "string"
      ? payload.message
      : undefined;
    const returnsAt = payload && typeof payload.returns_at === "string"
      ? payload.returns_at
      : undefined;
    const onlyMessageChange = next === maintenance && (message !== undefined || returnsAt !== undefined);
    try {
      const body = { enabled: next };
      if (message !== undefined) body.message = message;
      if (returnsAt !== undefined) body.returns_at = returnsAt;
      const r = await axios.post(
        `${API}/site/maintenance`,
        body,
        { headers: authHeaders() }
      );
      setMaintenance(!!r.data.maintenance);
      setMaintenanceMsg(r.data.message || "");
      setMaintenanceReturnsAt(r.data.returns_at || null);
      if (onlyMessageChange) {
        toast.success("STAND-BY DETAILS UPDATED");
      } else {
        toast.success(next ? "EPG UPDATE — site offline to viewers" : "BACK ON AIR — viewers can tune in");
      }
    } catch (e) {
      toast.error("MAINTENANCE TOGGLE FAILED", { description: e.response?.data?.detail || e.message });
    }
  }, [maintenance]);

  // Phase 2.3.62.3-r3-r2 — conservative authoritative clock refresh.
  // Refreshing the target once per minute is enough to recover from browser
  // throttling/sleep/buffering without turning playback into a constant seek.
  useEffect(() => {
    if (
      !power ||
      mode !== "tv" ||
      !liveTuneActive ||
      !currentChannel ||
      currentChannel.virtual ||
      currentChannel.isSubChannel ||
      currentVideos.length === 0
    ) {
      return undefined;
    }

    let cancelled = false;

    const refreshLiveClock = async () => {
      if (cancelled) return;
      const result = await tuneToLiveChannel(currentChannel, currentVideos);
      if (!result.ok && !result.stale) {
        try {
          window.__sltvBroadcastDriftStatus = {
            state: "CLOCK_REFRESH_FAILED",
            channelId: currentChannel.id,
            at: new Date().toISOString(),
          };
        } catch (_) {}
      }
    };

    const timer = setInterval(refreshLiveClock, 60000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [
    power,
    mode,
    liveTuneActive,
    currentChannel,
    currentVideos,
    tuneToLiveChannel,
  ]);

  const handleVideoEnded = useCallback(() => {
    if (currentVideo) {
      markWatched(currentVideo.id);
      setWatchedTick((t) => t + 1);
    }

    // Phase 2.3.62.3-r3-r2 — live programme boundaries belong to the
    // authoritative backend wall clock, never to a local index increment.
    if (
      liveTuneActive &&
      currentChannel &&
      currentChannel.id &&
      !currentChannel.virtual &&
      !currentChannel.isSubChannel &&
      currentVideos.length > 0
    ) {
      const handoffFromVideoId = currentVideo?.id || null;

      try {
        window.__sltvProgrammeHandoffStatus = {
          state: "RESOLVING",
          channelId: currentChannel.id,
          fromVideoId: handoffFromVideoId,
          at: new Date().toISOString(),
        };
      } catch (_) {}

      void (async () => {
        const result = await tuneToLiveChannel(currentChannel, currentVideos);

        if (result.ok) {
          setProgrammeHandoffTick((tick) => tick + 1);
          try {
            window.__sltvProgrammeHandoffStatus = {
              state: "LIVE_PROGRAMME_HANDOFF",
              channelId: currentChannel.id,
              fromVideoId: handoffFromVideoId,
              at: new Date().toISOString(),
            };
          } catch (_) {}
          return;
        }

        if (result.stale) {
          try {
            window.__sltvProgrammeHandoffStatus = {
              state: "SUPERSEDED",
              channelId: currentChannel.id,
              fromVideoId: handoffFromVideoId,
              at: new Date().toISOString(),
            };
          } catch (_) {}
          return;
        }

        try {
          window.__sltvProgrammeHandoffStatus = {
            state: "CLOCK_FAILED_LOCAL_FALLBACK",
            channelId: currentChannel.id,
            fromVideoId: handoffFromVideoId,
            at: new Date().toISOString(),
          };
        } catch (_) {}

        setVideoIndex((idx) => {
          const total = currentVideos.length;
          if (total === 0) return 0;
          return (idx + 1) % total;
        });
        setInitialSeekSec(0);
        setLiveTuneActive(false);
      })();
      return;
    }

    setVideoIndex((idx) => {
      const total = currentVideos.length;
      if (total === 0) return 0;
      const next = (idx + 1) % total;
      if (currentChannel && currentChannel.id) {
        channelStateRef.current.set(currentChannel.id, { tapeIdx: next });
        persistChannelState();
      }
      return next;
    });
    setInitialSeekSec(0);
  }, [
    currentVideos,
    currentVideo,
    currentChannel,
    liveTuneActive,
    tuneToLiveChannel,
  ]);

  // D-pad left/right → step through programmes within the current channel.
  // Phase 2.3.58r3: this deliberately mirrors natural auto-advance exactly:
  // changing programme is NOT changing channel, so there is no tuner/static
  // transition. Only genuine channel navigation owns the retune effect.
  const stepVideo = useCallback((dir) => {
    if (!power) return;
    const total = currentVideos.length;
    if (total < 2) return; // nothing to step through

    // Manual programme stepping is explicitly on-demand. Leaving LIVE here
    // prevents the periodic clock refresh from snapping the viewer straight
    // back to the scheduled programme.
    setLiveTuneActive(false);
    try {
      window.__sltvBroadcastTuneStatus = {
        state: "ON_DEMAND",
        channelId: currentChannel?.id || null,
        reason: "manual-programme-step",
      };
    } catch (_) {}

    setVideoIndex((idx) => {
      const next = ((idx + dir) % total + total) % total;
      if (currentChannel && currentChannel.id) {
        channelStateRef.current.set(currentChannel.id, { tapeIdx: next });
        persistChannelState();
      }
      return next;
    });
    setInitialSeekSec(0);
  }, [power, currentVideos.length, currentChannel]);

  // D-pad up/down — when the GUIDE is open, defer to it (the guide owns
  // its own list-cursor today); otherwise fall through to channel ↑/↓
  // for the same one-handed feel as a real TV remote.
  const dpadUp = useCallback(() => {
    if (mode === "guide") {
      window.dispatchEvent(new CustomEvent("crt:guide-nav", { detail: { direction: "up" } }));
      return;
    }
    changeChannel(1);
  }, [mode, changeChannel]);
  const dpadDown = useCallback(() => {
    if (mode === "guide") {
      window.dispatchEvent(new CustomEvent("crt:guide-nav", { detail: { direction: "down" } }));
      return;
    }
    changeChannel(-1);
  }, [mode, changeChannel]);


  const openUpload = useCallback(() => {
    if (!power) return;
    if (!isOwner) {
      toast.error("READ-ONLY MODE", { description: "Only the owner can program tapes." });
      return;
    }
    setMode((m) => (m === "upload" ? "tv" : "upload"));
  }, [power, isOwner]);

  const openGuide = useCallback(() => {
    if (!power) return;
    guideEntryRef.current = {
      channelId: currentChannel?.id || null,
      videoIndex,
    };
    setMode("guide");
  }, [power, currentChannel?.id, videoIndex]);

  const closeGuideWithoutTune = useCallback(() => {
    const entry = guideEntryRef.current;
    if (entry?.channelId) {
      const idx = channels.findIndex((c) => c.id === entry.channelId);
      if (idx >= 0 && idx !== channelIndex) setChannelIndex(idx);
      if (Number.isInteger(entry.videoIndex) && entry.videoIndex !== videoIndex) {
        setVideoIndex(entry.videoIndex);
      }
    }
    guideEntryRef.current = null;
    setMode("tv");
  }, [channels, channelIndex, videoIndex]);

  const toggleGuide = useCallback(() => {
    if (!power) return;
    if (mode === "guide") closeGuideWithoutTune();
    else openGuide();
  }, [power, mode, openGuide, closeGuideWithoutTune]);

  // Phase 2.3.4 - interactive first-visit tour replaces the old
  // automatic HELP popup. Viewer-only, maintenance-safe and notice-safe.
  useEffect(() => {
    if (!power || !ownerChecked || !maintenanceChecked || isOwner || maintenance) return;
    const hasUnackedNotice = !!(notice && notice.enabled && noticeAckedId !== notice.notice_id);
    if (hasUnackedNotice) return;

    try {
      if (localStorage.getItem("crt_tour_seen_v1")) return;
    } catch {
      return;
    }

    const t = setTimeout(() => setTourOpen(true), 1200);
    return () => clearTimeout(t);
  }, [power, ownerChecked, maintenanceChecked, isOwner, maintenance, notice, noticeAckedId]);

  const finishFirstVisitTour = useCallback(() => {
    try {
      localStorage.setItem("crt_tour_seen_v1", "1");
      // Phase 2.3.19 — only viewers who pass through the refreshed first-visit
      // onboarding become eligible for the one-time Guide introduction.
      // Existing viewers therefore aren't unexpectedly shown a "first visit"
      // Guide tour after this release.
      localStorage.setItem("crt_guide_tour_eligible_v1", "1");
      // The Welcome Tutorial already explained this release, so do not
      // immediately repeat it as a WHAT'S NEW announcement.
      localStorage.setItem("crt_whats_new_series_info_2_3_81", "1");
    } catch { /* private mode / quota - completion still closes this session */ }
    setTourOpen(false);
  }, []);

  // One versioned announcement for established viewers, including the owner.
  // For genuinely new viewers the Welcome Tutorial wins the initial race and
  // marks this release seen when onboarding ends, preventing a duplicate.
  useEffect(() => {
    if (!power || !ownerChecked || !maintenanceChecked || maintenance || tourOpen) return;
    const hasUnackedNotice = !!(notice && notice.enabled && noticeAckedId !== notice.notice_id);
    if (hasUnackedNotice) return;

    try {
      const alreadySeen = localStorage.getItem(WHATS_NEW_SERIES_INFO_KEY) === "1";
      if (alreadySeen) return;
    } catch {
      return;
    }

    const timer = setTimeout(() => setWhatsNewOpen(true), 1400);
    return () => clearTimeout(timer);
  }, [power, ownerChecked, maintenanceChecked, maintenance, tourOpen, notice, noticeAckedId]);

  const dismissWhatsNew = useCallback(() => {
    try { localStorage.setItem(WHATS_NEW_SERIES_INFO_KEY, "1"); } catch { /* session dismissal still works */ }
    setWhatsNewOpen(false);
  }, []);

  const showSeriesInfoFromWhatsNew = useCallback(() => {
    try { localStorage.setItem(WHATS_NEW_SERIES_INFO_KEY, "1"); } catch { /* continue for this session */ }
    setWhatsNewOpen(false);
    if (mode === "tv" && currentVideo?.show_title) setSeriesInfoOpen(true);
  }, [mode, currentVideo?.show_title]);

  // ─── Cabinet scale — auto-detect viewport + honour PREFS override ─
  // The CRT + Remote pair is hard-capped at ~995px + 240px which looks
  // right on 1440p but tiny on 2560/4K monitors. We publish a
  // `--cabinet-scale` CSS var on the outer layout wrapper; both
  // components multiply their max-width against it. AUTO mode maps
  // viewport width into a 1x-1.7x range using 1440px as the baseline.
  // Manual override (viewer's `cabinet_scale` pref, 0.75-1.75) wins
  // when set.
  const [autoCabinetScale, setAutoCabinetScale] = useState(1);
  const [manualCabinetScale, setManualCabinetScale] = useState(() => {
    try { return getPrefs().cabinet_scale ?? null; } catch { return null; }
  });
  // Live-preview override while the PREFS CABINET SIZE slider is being
  // dragged. Three-state: `undefined` = no active preview (use the
  // persisted value below); `null` = previewing AUTO; a number =
  // previewing that manual scale. Cleared by the panel-close event so
  // dismissing PREFS without APPLY reverts to the saved value.
  const [previewCabinetScale, setPreviewCabinetScale] = useState(undefined);
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Cabinet natively lives at ~1300px wide × ~800px tall (CRT 995 +
      // gap 64 + remote 240). Scale up gently on larger displays, but
      // constrain by BOTH dimensions so we never grow past what the
      // viewport can fit. Cap at 1.25× (was 1.4× — users reported the
      // cabinet still overflowed on 1080-tall monitors at 1.4). Also
      // ROUND to the nearest 0.05 so the CRT width lands on integer
      // pixels — Firefox anti-aliases the `.scanlines` stripe pattern
      // when the parent transform + fractional widths compound, which
      // washes the scanlines out. Snapping to 5% increments gives
      // predictable pixel-aligned dimensions (995 * 1.10 = 1094.5,
      // 995 * 1.15 = 1144.25, etc — remaining halves still lose a
      // sub-pixel but the pattern is dramatically more stable than
      // the raw `w/1700` output).
      const wf = Math.min(1.25, Math.max(1, w / 1700));
      const hf = Math.min(1.25, Math.max(1, h / 950));
      const raw = Math.min(wf, hf);
      const snapped = Math.round(raw * 20) / 20; // nearest 0.05
      setAutoCabinetScale(snapped);
      // Broadcast + cache on window so ViewerSettings can park its
      // slider thumb at the current effective value while in AUTO
      // mode. The window cache is critical because ViewerSettings can
      // mount AFTER App.js has already dispatched (e.g. after
      // power-on / guide-open causes a re-render), so a late listener
      // would otherwise miss the initial value and fall back to its
      // useState default (1 → 100%).
      window.__crtAutoCabinetScale = snapped;
      window.dispatchEvent(new CustomEvent("crt:auto-cabinet-scale", { detail: snapped }));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);
  useEffect(() => {
    const refresh = () => {
      try { setManualCabinetScale(getPrefs().cabinet_scale ?? null); }
      catch { setManualCabinetScale(null); }
    };
    window.addEventListener("crt:prefs-updated", refresh);
    return () => window.removeEventListener("crt:prefs-updated", refresh);
  }, []);
  // Cabinet scale live-preview — the PREFS slider dispatches
  // `crt:cabinet-scale-preview` on every drag tick (detail = number or
  // null for AUTO) so the CRT + remote resize immediately. On panel
  // close without APPLY, `crt:cabinet-scale-preview-end` fires and we
  // drop back to the persisted value.
  useEffect(() => {
    const onPreview = (e) => setPreviewCabinetScale(e.detail);
    const onPreviewEnd = () => setPreviewCabinetScale(undefined);
    window.addEventListener("crt:cabinet-scale-preview", onPreview);
    window.addEventListener("crt:cabinet-scale-preview-end", onPreviewEnd);
    return () => {
      window.removeEventListener("crt:cabinet-scale-preview", onPreview);
      window.removeEventListener("crt:cabinet-scale-preview-end", onPreviewEnd);
    };
  }, []);
  // Effective scale = live-preview (if set) → manual pin → auto. Preview
  // wins even when it's `null` (AUTO preview), so a viewer sliding to
  // AUTO sees the automatic size immediately.
  const activeManualScale = previewCabinetScale === undefined ? manualCabinetScale : previewCabinetScale;
  const cabinetScale = (
    activeManualScale != null && Number.isFinite(activeManualScale)
      ? Math.max(0.75, Math.min(1.75, activeManualScale))
      : autoCabinetScale
  );


  // Body-class toggle by mode — powers the "hide scanlines when a
  // menu/guide is open" CSS rule (see App.css). Each mode gets its
  // own class so future per-mode styling can hook in cleanly.
  useEffect(() => {
    const cls = mode === "guide" ? "guide-open"
              : mode === "upload" ? "upload-open"
              : mode === "updates" ? "updates-open"
              : null;
    if (!cls) return undefined;
    document.body.classList.add(cls, "menu-open");
    return () => document.body.classList.remove(cls, "menu-open");
  }, [mode]);

  // Body-class toggle for owner-selected layout mode ("bezel" default,
  // "fullscreen" escapes overlays from the CRT tube). Applied at boot
  // and whenever the owner changes the setting — every viewer picks
  // up the new mode on their next boot / setting-poll.
  useEffect(() => {
    if (layoutMode === "fullscreen") {
      document.body.classList.add("layout-fullscreen");
      return () => document.body.classList.remove("layout-fullscreen");
    }
    document.body.classList.remove("layout-fullscreen");
    return undefined;
  }, [layoutMode]);

  // Fetch the layout mode on boot AND poll every 15s so any viewer
  // (anonymous or logged-in) picks up the owner's mode change within
  // the poll window — no manual reload needed. Public endpoint, no
  // auth. Server is the single source of truth so bezel/fullscreen is
  // truly universal across every session.
  useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const { data } = await axios.get(`${API}/site/layout`);
        if (!cancelled && data?.mode) {
          setLayoutMode((prev) => (prev === data.mode ? prev : data.mode));
        }
      } catch (e) { /* fall back to whatever we had */ }
    };
    pull();
    const iv = setInterval(pull, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Owner-only toggle. Optimistically flips local state, then persists.
  // Reverts on failure so a viewer isn't stuck with a half-applied mode.
  const setLayoutModeRemote = useCallback(async (nextMode) => {
    if (!isOwner) return;
    if (nextMode !== "bezel" && nextMode !== "fullscreen") return;
    const prev = layoutMode;
    setLayoutMode(nextMode);
    try {
      await axios.post(`${API}/site/layout`, { mode: nextMode }, { headers: authHeaders() });
    } catch (e) {
      setLayoutMode(prev);
    }
  }, [isOwner, layoutMode]);

  const tuneToChannel = useCallback((channelId, vidIdx = null) => {
    setChannels((cs) => {
      const idx = cs.findIndex((c) => c.id === channelId);
      if (idx >= 0) {
        // Track previous channel for the "LAST" toggle (skip if it's the
        // same channel — re-tuning the current channel doesn't count as a
        // hop). Excludes virtuals because LAST is a physical-cabinet idiom
        // and virtual lanes feel awkward in the back-button history.
        setChannelIndex((prevIdx) => {
          if (prevIdx !== idx) {
            const prevCh = cs[prevIdx];
            if (prevCh && !prevCh.virtual) previousChannelIdRef.current = prevCh.id;
          }
          return idx;
        });

        const targetCh = cs[idx];
        const targetVids = videosByChannel[channelId] || [];
        const explicitProgramme = Number.isInteger(vidIdx);

        if (explicitProgramme) {
          const pick = Math.max(0, Math.min(vidIdx, Math.max(0, targetVids.length - 1)));
          setVideoIndex(pick);
          channelStateRef.current.set(channelId, { tapeIdx: pick });
          persistChannelState();
          setInitialSeekSec(0);
          setLiveTuneActive(false);
        } else if (targetCh && !targetCh.virtual && !targetCh.isSubChannel && targetVids.length > 0) {
          void (async () => {
            const result = await tuneToLiveChannel(targetCh, targetVids);
            if (!result.ok && !result.stale) {
              const fallback = getOrPickTapeIndex(channelId, targetVids);
              setVideoIndex(fallback);
              setInitialSeekSec(0);
              setLiveTuneActive(false);
            }
          })();
        } else {
          const fallback = getOrPickTapeIndex(channelId, targetVids);
          setVideoIndex(fallback);
          setInitialSeekSec(0);
          setLiveTuneActive(false);
        }

        triggerStatic();
        triggerChannelOSD();
      }
      return cs;
    });
  }, [triggerStatic, triggerChannelOSD, videosByChannel, tuneToLiveChannel]);

  // LAST channel toggle — bounces back to the channel the viewer most
  // recently tuned away from. No-op until at least one channel change has
  // happened (boot state). Reuses tuneToChannel so the static glitch /
  // OSD / tape resume all behave exactly like a normal tune.
  const tuneToLast = useCallback(() => {
    if (!power) return;
    const prevId = previousChannelIdRef.current;
    if (!prevId) return;
    const idx = channels.findIndex((c) => c.id === prevId && !c.virtual);
    if (idx < 0) {
      // The previous channel was deleted while the viewer was away — drop
      // the stale ref so subsequent LAST presses are silent no-ops instead
      // of bouncing to a ghost.
      previousChannelIdRef.current = null;
      return;
    }
    tuneToChannel(prevId);
  }, [power, channels, tuneToChannel]);

  const handleVideoUploaded = useCallback(async (channelId) => {
    await fetchChannels();
    if (channelId) tuneToChannel(channelId);
  }, [fetchChannels, tuneToChannel]);

  const handleChannelCreated = useCallback(async () => {
    await fetchChannels();
  }, [fetchChannels]);

  const handleRestoreOwner = useCallback(async (token) => {
    try {
      const v = await axios.post(`${API}/owner/verify`, { token });
      if (v.data.valid) {
        localStorage.setItem(OWNER_KEY_STORAGE, token);
        setIsOwner(true);
        toast.success("OWNER ACCESS RESTORED");
        return true;
      }
      toast.error("INVALID OWNER KEY");
      return false;
    } catch (e) {
      toast.error("VERIFY FAILED");
      return false;
    }
  }, []);

  const handleSignOutOwner = useCallback(() => {
    localStorage.removeItem(OWNER_KEY_STORAGE);
    setIsOwner(false);
    setMode("tv");
    toast.success("OWNER SIGNED OUT — VIEWER MODE");
  }, []);

  // Phase 2.3.55 — canonical friendly URL mirror.
  useEffect(() => {
    if (!initialRandomizedRef.current || !currentChannel || currentChannel.virtual) return;

    // Preserve the neutral catalogue URL. Exact current channel/video are still
    // persisted below so refreshing /programmes resumes deterministically.
    const neutralEntry = window.__SCARTLEAD_DEEPLINK__;
    const preserveProgrammes =
      neutralEntry?.neutral_entry === true &&
      neutralEntry?.canonical_path === "/programmes" &&
      window.location.pathname === "/programmes";

    const preserveSeries =
      neutralEntry?.kind === "series" &&
      typeof neutralEntry?.canonical_path === "string" &&
      neutralEntry.canonical_path.startsWith("/series/") &&
      window.location.pathname === neutralEntry.canonical_path &&
      Number(neutralEntry?.channel_number) === Number(currentChannel?.number);

    let cancelled = false;

    // Phase 2.3.62.3-r4 — the URL describes what the viewer CHOSE, not merely
    // whichever programme happens to be airing at this instant.
    //
    // LIVE linear viewing → canonicalise the CHANNEL only. Programme changes
    // underneath it without rewriting the address bar to /watch/...
    //
    // Explicit/on-demand viewing → include video_id so the canonical endpoint
    // deliberately returns /watch/<programme-slug>.
    const qs = new URLSearchParams({ channel_id: currentChannel.id });
    if (!liveTuneActive && currentVideo && currentVideo.id) {
      qs.set("video_id", currentVideo.id);
    }

    (async () => {
      try {
        const res = await fetch(`${API}/share/canonical?${qs.toString()}`);
        if (!res.ok) return;
        const body = await res.json();
        if (
          !preserveProgrammes &&
          !preserveSeries &&
          !cancelled &&
          body?.path &&
          (window.location.pathname !== body.path || window.location.search)
        ) {
          window.history.replaceState({}, "", body.path);
        }
      } catch (e) {}
    })();

    try {
      localStorage.setItem("crt_last_channel_id", currentChannel.id);
      if (currentVideo?.id) localStorage.setItem("crt_last_video_id", currentVideo.id);
    } catch (e) {}

    return () => { cancelled = true; };
  }, [currentChannel, currentVideo, liveTuneActive]);


  const handleShare = useCallback(() => {
    if (!currentChannel || currentChannel.virtual) return;
    setShareOpen(true);
  }, [currentChannel]);

  // Phase 2.3.79g-r3.4 — direct mobile utility actions.
  useEffect(() => {
    const onMobileShare = () => { handleShare(); };
    const onMobileTrophies = () => { setShowTrophies(true); };
    const onMobileRestoreOwner = () => {
      const token = window.prompt("ENTER OWNER KEY");
      if (!token || !token.trim()) return;
      handleRestoreOwner(token.trim());
    };
    window.addEventListener("sltv:mobile-share", onMobileShare);
    window.addEventListener("sltv:mobile-open-trophies", onMobileTrophies);
    window.addEventListener("sltv:mobile-restore-owner", onMobileRestoreOwner);
    return () => {
      window.removeEventListener("sltv:mobile-share", onMobileShare);
      window.removeEventListener("sltv:mobile-open-trophies", onMobileTrophies);
      window.removeEventListener("sltv:mobile-restore-owner", onMobileRestoreOwner);
    };
  }, [handleShare, handleRestoreOwner]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "ArrowUp") {
        if (mode === "guide") return;
        e.preventDefault();
        changeChannel(1);
      }
      else if (e.key === "ArrowDown") {
        if (mode === "guide") return;
        e.preventDefault();
        changeChannel(-1);
      }
      // ← / → seek the tape ±10 seconds. This matches Firefox's native
      // <video> keyboard behaviour and is the more useful shortcut for
      // a station whose channels ARE video playlists. Volume-by-arrow
      // was removed — VOLUME + / − on the remote (and the physical
      // volume keys on media keyboards) still work fine. Dispatched
      // as a custom event so CRT.js can route it to whichever player
      // is active (YouTube / native / archive / MEGA).
      else if (e.key === "ArrowRight") {
        if (mode === "guide") return;
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("crt:seek-relative", { detail: 10 }));
      }
      else if (e.key === "ArrowLeft") {
        if (mode === "guide") return;
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("crt:seek-relative", { detail: -10 }));
      }
      else if (e.key.toLowerCase() === "p") { togglePower(); }
      // Shift+M opens the owner's SITE MESSAGES menu (no-op for viewers).
      // Must come BEFORE the plain "m" branch — otherwise toLowerCase()
      // would swallow the shifted variant. Decoupled from the component
      // tree via a window event consumed by OwnerBar.
      else if (e.key === "M" && e.shiftKey) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("crt:open-messages-menu"));
      }
      else if (e.key.toLowerCase() === "m") { openUpload(); }
      else if (e.key.toLowerCase() === "g") { toggleGuide(); }
      // `/` opens the Guide and focuses its search input. The Guide
      // component handles focus itself when it mounts with a query
      // arriving via the `crt:guide-focus-search` custom event so
      // keyboard nav lives entirely inside the component tree.
      else if (e.key === "/") {
        e.preventDefault();
        if (mode !== "guide") openGuide();
        // Fire on next microtask so the Guide has mounted its input ref.
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("crt:guide-focus-search"));
        }, 0);
      }
      // `i` (info) and Space re-summon the NOW BROADCASTING banner + COMING
      // UP strip without forcing a full channel change. Mirrors the remote's
      // SELECT button so viewers can flash a reminder of what's playing.
      else if (e.key.toLowerCase() === "i" || e.key === " " || e.code === "Space") {
        e.preventDefault();
        handleSelectInfo();
      }
      // Digit keys 0-9 feed the channel-buffer hook. We use `e.key` rather
      // than `e.code` so the top-row digits AND the numeric keypad both
      // work, and we ignore the press if a modifier is held so combos
      // like Ctrl+1 (browser tab) still bubble.
      else if (/^[0-9]$/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        pushDigit(e.key);
      }
      // L → LAST channel toggle. Mirrors the remote's LAST button so
      // power users can bounce between two channels without picking up
      // the mouse.
      else if (e.key.toLowerCase() === "l" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        tuneToLast();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [changeChannel, togglePower, openUpload, toggleGuide, openGuide, handleSelectInfo, pushDigit, tuneToLast, maintenance, maintenanceChecked, ownerChecked, isOwner]);

  return (
    <div className="App relative min-h-screen w-full overflow-hidden bg-black" data-testid="app-root" data-mode={mode}>
      {/* Seasonal decoration overlay — site-wide festive drift of
          snowflakes / bats / hearts / etc. controlled by the owner
          via the Owner Bar Theme panel (active_season field). Renders
          null when active_season is `none`. Fixed-position and click-
          through over the universal black stage, below the CRT/UI. */}
      <SeasonalOverlay season={theme.active_season} />
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: "#050505",
            border: "1px solid var(--phosphor)",
            color: "var(--phosphor)",
            fontFamily: "JetBrains Mono, monospace",
          },
        }}
      />

      {/* Viewer personal-prefs panel — visible to ALL visitors (owner +
          anonymous viewers). Sits in the top-left so it doesn't fight the
          OwnerBar cluster on the right. Hidden while a fullscreen menu
          overlay is up because those overlays already own the whole
          viewport chrome (search box + ESC + owner pills) — otherwise
          PREFS blends into the guide header and reads as broken. */}
      {ownerChecked && (isOwner || (maintenanceChecked && !maintenance)) && !(layoutMode === "fullscreen" && mode !== "tv" && mode !== "guide") && (
  <div
        className="sltv-viewer-settings-host sltv-guide-prefs-tools absolute top-4 left-4 z-50 flex items-center gap-2"
        style={{ fontFamily: "JetBrains Mono, monospace" }}
      >
        <ViewerSettings channels={channels} />
        </div>
      )}

      {!(layoutMode === "fullscreen" && mode !== "tv" && mode !== "guide") && (
      <div className="sltv-guide-owner-tools">
      <OwnerBar
        isOwner={isOwner}
        ownerChecked={ownerChecked}
        onShare={handleShare}
        onRestore={handleRestoreOwner}
        onSignOut={handleSignOutOwner}
        getToken={getStoredToken}
        maintenance={maintenance}
        maintenanceMessage={maintenanceMsg}
        maintenanceReturnsAt={maintenanceReturnsAt}
        onToggleMaintenance={handleToggleMaintenance}
        notice={notice}
        onPublishNotice={handlePublishNotice}
        mobileWarning={mobileWarning}
        onSaveMobileWarning={handleSaveMobileWarning}
        viewerTips={viewerTips}
        onSaveViewerTips={handleSaveViewerTips}
        welcomeMessage={welcomeMessage}
        onSaveWelcomeMessage={handleSaveWelcomeMessage}
        updatesData={updatesData}
        onSaveUpdatesNotes={handleSaveUpdatesNotes}
        onSaveViewerUpdates={handleSaveViewerUpdates}
        onOpenTrophies={() => setShowTrophies(true)}
        trophyPulseTick={trophyPulse}
        trophySettings={trophySettings}
        onSaveTrophySettings={handleSaveTrophySettings}
        onUploadTrophySound={handleUploadTrophySound}
        onClearTrophySound={handleClearTrophySound}
        pledgeDrive={pledgeDrive}
        onSavePledgeDrive={handleSavePledgeDrive}
        channels={channels}
        videosByChannel={videosByChannel}
        apiBase={API}
        authHeaders={authHeaders}
      />
      </div>
      )}

      {/* First-visit guided tour - viewer-only onboarding. */}
      <FirstVisitTour
        open={tourOpen && ownerChecked && maintenanceChecked && !isOwner && !maintenance}
        onFinish={finishFirstVisitTour}
        onSkip={finishFirstVisitTour}
      />

      {/* Phase 2.3.81b — one-time feature discovery for established viewers. */}
      <WhatsNew
        open={whatsNewOpen && !tourOpen && ownerChecked && maintenanceChecked && !maintenance}
        canShowSeries={mode === "tv" && !!currentVideo?.show_title}
        onShow={showSeriesInfoFromWhatsNew}
        onDismiss={dismissWhatsNew}
      />

      {/* Tribute — 24h "▲ IN MEMORIAM" banner at top of viewport
          whenever a fresh tribute is published (auto-hides on
          dismiss or after 24h). Clicking it opens the fullscreen
          respectful memorial page. Banner is hidden during
          maintenance so it doesn't stack on top of the stand-by
          overlay. */}
      {!maintenance && tribute && tribute.enabled && (
        <TributeBanner tribute={tribute} onOpen={() => setShowTribute(true)} />
      )}
      {showTribute && tribute && tribute.enabled && (
        <TributeView
          tribute={tribute}
          channels={channels}
          onClose={() => setShowTribute(false)}
          onTuneChannel={(cid) => {
            // Phase 2.3.62.3-r4 — a tribute CHANNEL choice is a normal linear
            // tune and therefore joins the backend-authoritative live clock.
            tuneToChannel(cid);
            setMode("tv");
            setShowTribute(false);
          }}
          onOpenTape={(cid, vid) => {
            // A tribute PROGRAMME choice is explicit/on-demand. Route it
            // through tuneToChannel(cid, vidIdx) so LIVE mode is disabled and
            // the canonical mirror intentionally becomes /watch/<slug>.
            const vids = videosByChannel[cid] || [];
            const vidx = vids.findIndex((v) => v.id === vid);
            if (vidx >= 0) {
              tuneToChannel(cid, vidx);
              setMode("tv");
              setShowTribute(false);
            }
          }}
        />
      )}

      <TrophyRoom
        open={showTrophies}
        onClose={() => setShowTrophies(false)}
        refreshTick={trophyTick}
        overrides={trophySettings.overrides || {}}
        disabled={trophySettings.disabled || []}
      />

      {/* PHANTOM SIGNAL — Konami payoff overlay. 4s of static + green text. */}
      {showPhantom && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none"
          data-testid="phantom-signal"
        >
          <div
            className="absolute inset-0 static-noise opacity-60"
            aria-hidden="true"
          />
          <div
            className="relative text-[var(--phosphor)] phosphor-glow chromatic text-4xl md:text-6xl tracking-[0.4em] text-center"
            style={{ fontFamily: "VT323, monospace" }}
          >
            <div>◢ PHANTOM ◣</div>
            <div className="mt-2">SIGNAL DETECTED</div>
          </div>
        </div>
      )}

      {/* Mobile / small-viewport gate — shows above EVERYTHING else (incl.
          maintenance and notice overlays) so phone users hit it first. Once
          they tap CONTINUE ANYWAY the dismiss is stored in sessionStorage,
          so re-opening the tab will re-prompt. Width threshold 1024px also
          catches tablets in portrait. */}
      {/* Phase 2.3.79 — phones/tablets are now first-class viewers.
          Keep the legacy warning state/configuration code in place for safe
          rollback, but do not block small viewports from entering the app. */}
      {false && isNarrow && !mobileDismissed && (
        <MobileWarning
          onContinue={handleMobileContinue}
          title={mobileWarning.title}
          body={mobileWarning.body}
        />
      )}

      {/* Updates "checking-for-updates → what's new" now renders INSIDE
          the CRT screen as a `mode === "updates"` view (see <CRT> children
          below). The full-page overlay was retired here so the boot
          sequence feels like the cabinet itself doing the update, not a
          modal layered over the room. The maintenance + notice gates
          followed the same migration in (k); they now render inside the
          CRT as well — see the <CRT> children block. */}

      {/* phase-2.3.81a-r3-series-info — viewer-invoked, outside CRT geometry. */}
      {seriesInfoOpen && currentVideo?.show_title && currentChannel && (
        <SeriesInfo
          showTitle={currentVideo.show_title}
          channel={currentChannel}
          videos={videosByChannel[currentChannel.id] || []}
          currentVideo={currentVideo}
          onClose={() => setSeriesInfoOpen(false)}
          onTune={(videoId) => {
            const list = videosByChannel[currentChannel.id] || [];
            const idx = list.findIndex((v) => v.id === videoId);
            if (idx >= 0) {
              tuneToChannel(currentChannel.id, idx);
              setMode("tv");
              setSeriesInfoOpen(false);
            }
          }}
        />
      )}

      <div
        className="sltv-main-stage relative z-10 min-h-screen w-full flex flex-col xl:flex-row items-center justify-center gap-10 xl:gap-16 p-4 md:p-10 pt-20 md:pt-16"
        style={{ "--cabinet-scale": cabinetScale }}
      >
        <CRT
          power={power}
          isStatic={isStatic}
          mode={mode}
          currentChannel={currentChannel}
          currentVideo={currentVideo}
          channelNumber={currentChannel ? currentChannel.number : 0}
          channelNumberLabel={currentChannel ? currentChannel.numberLabel : null}
          channelName={currentChannel ? currentChannel.name : ""}
          channelIsSubChannel={currentChannel ? !!currentChannel.isSubChannel : false}
          channelParentName={currentChannel ? (currentChannel.parent_name || "") : ""}
          channelDescription={currentChannel ? currentChannel.description : ""}
          videoCount={currentVideos.length}
          videoIndex={videoIndex}
          playlist={currentVideos}
          initialSeekSec={initialSeekSec}
          liveTuneActive={liveTuneActive}
          selectTick={selectTick}
          programmeHandoffTick={programmeHandoffTick}
          peekTick={peekTick}
          epgPeek={epgPeek}
          scanlineLevel={scanlineLevel}
          scanlineLabel={SCANLINE_LABELS[scanlineLevel]}
          viewerTips={viewerTips}
          welcomeMessage={welcomeMessage}
          volume={volume}
          onVolumeChange={(next) => { setVolume(Math.max(0, Math.min(MAX_VOLUME, Number(next) || 0))); triggerVolumeOSD(); }}
          onStepVideo={stepVideo}
          osdChannel={osdChannel}
          osdVolume={osdVolume}
          digitBuffer={digitBuffer}
          onVideoEnded={handleVideoEnded}
          apiBase={API}
          isOwner={isOwner}
          maintenanceSuspended={maintenance && !isOwner}
          maintenanceWakeOnly={maintenance && !isOwner && maintenanceForcedPowerRef.current}
          brandText={theme.brand_text}
          callsign={theme.callsign}
          reduceMotion={!!viewerPrefs.reduce_motion}
          silentAutoSkip={!!viewerPrefs.silent_auto_skip}
          powerOnAnimation={viewerPrefs.power_on_animation !== false}
          tubeGlow={true}
          tubeGlowIntensity={viewerPrefs.tube_glow_intensity}
          tubeGlowColor={viewerPrefs.tube_glow_color}
          tubeGlowAnimated={!!viewerPrefs.tube_glow_animated}
          tubeGlowAudioReactive={!!viewerPrefs.tube_glow_audio_reactive}
          pictureMode={pictureMode}
          authUser={authUser}
          whatsNew={whatsNew}
          channels={channels}
          videosByChannel={videosByChannel}
          lineupReady={lineupReady}
          audioTargetLufs={audioTargetLufs}
          pledgeDrive={pledgeDrive}
          hideDistractions={!!viewerPrefs.hide_distractions}
          overlay={{
            // Per-channel override beats site theme one field at a time —
            // null/undefined fields cascade up to the theme defaults so a
            // channel can carry just its own text while inheriting the
            // site style/position/opacity. Virtual channels (★ FAVOURITES,
            // ▶▶ CONTINUE WATCHING) never have an overlay of their own.
            enabled: (currentChannel && currentChannel.overlay && currentChannel.overlay.enabled !== null && currentChannel.overlay.enabled !== undefined)
              ? !!currentChannel.overlay.enabled
              : !!theme.live_overlay_enabled,
            text: (currentChannel && currentChannel.overlay && currentChannel.overlay.text)
              ? currentChannel.overlay.text
              : (theme.live_overlay_text || ""),
            style: (currentChannel && currentChannel.overlay && currentChannel.overlay.style)
              ? currentChannel.overlay.style
              : (theme.live_overlay_style || "boxed"),
            position: (currentChannel && currentChannel.overlay && currentChannel.overlay.position)
              ? currentChannel.overlay.position
              : (theme.live_overlay_position || "br"),
            opacity: (currentChannel && currentChannel.overlay && typeof currentChannel.overlay.opacity === "number")
              ? currentChannel.overlay.opacity
              : (typeof theme.live_overlay_opacity === "number" ? theme.live_overlay_opacity : 75),
          }}
          fireTrophy={fireTrophy}
          onPower={togglePower}
        >
          {/* ★ CONTACT virtual channel — when the viewer tunes here the
              CRT swaps the video player for the in-cabinet contact form.
              z-20 so it sits ABOVE the (intentionally blank) video plane
              but BELOW the maintenance/notice gates which need to take
              over even from inside a virtual channel. */}
          {power && currentChannel && currentChannel.kind === "contact" && mode === "tv" && (
            <ContactPanel />
          )}
          {mode === "upload" && power && isOwner && (
            <FullscreenPortal fullscreen={layoutMode === "fullscreen"}>
              <UploadTerminal
                channels={channels.filter((c) => !c.virtual)}
                categories={categories}
                videosByChannel={videosByChannel}
                currentChannelId={currentChannel && !currentChannel.virtual ? currentChannel.id : null}
                onClose={() => setMode("tv")}
                onUploaded={handleVideoUploaded}
                onChannelCreated={handleChannelCreated}
                onTuneToChannel={tuneToChannel}
                apiBase={API}
                authHeaders={authHeaders}
                ownerToken={getStoredToken()}
                onSignOut={handleSignOutOwner}
              />
            </FullscreenPortal>
          )}
          {mode === "guide" && power && (
            <FullscreenPortal
              fullscreen={layoutMode === "fullscreen"}
              reserveTopChrome={layoutMode === "fullscreen"}
            >
              <ChannelGuide
                channels={channels}
                categories={categories}
                videosByChannel={videosByChannel}
                currentChannelId={currentChannel ? currentChannel.id : null}
                currentVideo={currentVideo}
                guideNowSnapshot={epgPeek}
                guideScheduleWindow={guideScheduleWindow}
                onExtendScheduleWindow={extendGuideScheduleWindow}
                volume={volume}
                watched={getWatched()}
                watchedTick={watchedTick}
                viewerCounts={channelViewerCounts}
                welcomeBack={welcomeBackData}
                onWelcomeBackContinue={dismissWelcomeBack}
                onSelect={(id, vIdx) => { guideEntryRef.current = null; tuneToChannel(id, vIdx); setMode("tv"); dismissWelcomeBack(); }}
                onBrowseChannel={(chId) => { setBrowseChannelId(chId); setMode("browse"); }}
                onOpenTapeDetail={(vId) => { setDetailVideoId(vId); setMode("detail"); }}
                onClose={() => { closeGuideWithoutTune(); dismissWelcomeBack(); }}
                apiBase={API}
                isOwner={isOwner}
                authHeaders={authHeaders}
                onChannelArtChanged={fetchChannels}
                onVideosReordered={(channelId, newVideos) =>
                  setVideosByChannel((prev) => ({ ...prev, [channelId]: newVideos }))
                }
                layoutMode={layoutMode}
                onToggleLayoutMode={setLayoutModeRemote}
              />
            </FullscreenPortal>
          )}
          {mode === "browse" && power && browseChannelId && (() => {
            const ch = channels.find((c) => c.id === browseChannelId);
            const vids = videosByChannel[browseChannelId] || [];
            return (
              <FullscreenPortal fullscreen={layoutMode === "fullscreen"}>
                <ChannelBrowse
                  channel={ch}
                  videos={vids}
                  isOwner={isOwner}
                  apiBase={API}
                  authHeaders={authHeaders}
                  onBack={() => setMode("guide")}
                  onTune={(vIdx) => { tuneToChannel(browseChannelId, vIdx); setMode("tv"); }}
                  onOpenTape={(vId) => { setDetailVideoId(vId); setMode("detail"); }}
                  onVideosChanged={() => {
                    // Refetch so the guide/browse picks up new show_title
                    // stamps and re-derives sub-channels.
                    try { window.dispatchEvent(new Event("crt:videos-updated")); } catch (e) { /* noop */ }
                  }}
                />
              </FullscreenPortal>
            );
          })()}
          {mode === "detail" && power && detailVideoId && (() => {
            // Resolve the tape by scanning every channel's video list — the
            // detail page can be opened from anywhere so we don't assume
            // browseChannelId is still relevant.
            let found = null;
            let ownerCh = null;
            for (const cId of Object.keys(videosByChannel)) {
              const list = videosByChannel[cId] || [];
              const hit = list.find((v) => v.id === detailVideoId);
              if (hit) {
                found = hit;
                ownerCh = channels.find((c) => c.id === cId);
                break;
              }
            }
            const siblings = ownerCh ? (videosByChannel[ownerCh.id] || []) : [];
            return (
              <FullscreenPortal fullscreen={layoutMode === "fullscreen"}>
                <TapeDetail
                  video={found}
                  channel={ownerCh}
                  siblings={siblings}
                  currentPlayingId={currentVideo ? currentVideo.id : null}
                  onBack={() => setMode(browseChannelId ? "browse" : "guide")}
                  onTune={(vId) => {
                    if (!ownerCh) return;
                    const idx = siblings.findIndex((v) => v.id === vId);
                    if (idx >= 0) { tuneToChannel(ownerCh.id, idx); setMode("tv"); }
                  }}
                  onOpenTape={(vId) => setDetailVideoId(vId)}
                />
              </FullscreenPortal>
            );
          })()}
          {power && mode === "tv" && remoteVolumeOsd !== null && (
            <div
              className="absolute top-5 right-5 z-[95] pointer-events-none"
              data-testid="remote-volume-osd"
              aria-live="polite"
            >
              <div className="min-w-[190px] border border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] bg-black/95 px-4 py-3 shadow-[0_0_22px_rgba(0,0,0,0.8)]">
                <div className="flex items-center justify-between gap-5 mb-2">
                  <span className="text-[9px] tracking-[0.30em] opacity-65">VOLUME</span>
                  <span className="text-sm tracking-[0.12em] phosphor-glow tabular-nums">
                    {String(remoteVolumeOsd).padStart(2, "0")}/10
                  </span>
                </div>
                <div className="grid grid-cols-10 gap-1">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <span
                      key={i}
                      className={`block h-2 border border-[color-mix(in_srgb,var(--phosphor)_35%,transparent)] ${
                        i < remoteVolumeOsd ? "bg-[var(--phosphor)] shadow-[0_0_5px_var(--phosphor)]" : "bg-transparent"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {mode === "updates" && power && (() => {
            // /site/updates decides WHAT is recent; enrich those lightweight
            // rows from the already-loaded catalogue so the Updates screen
            // gets the same TMDb/poster/backdrop metadata as the Guide.
            const catalogueRows = Object.entries(videosByChannel).flatMap(
              ([catalogueChannelId, vids]) =>
                (Array.isArray(vids) ? vids : []).map((video, catalogueIndex) => ({
                  video,
                  catalogueChannelId,
                  catalogueIndex,
                }))
            );

            const catalogueByVideoId = new Map();
            for (const row of catalogueRows) {
              if (row.video && row.video.id && !catalogueByVideoId.has(row.video.id)) {
                catalogueByVideoId.set(row.video.id, row);
              }
            }

            const enrichedRecentVideos = (Array.isArray(updatesData.recent_videos)
              ? updatesData.recent_videos
              : []
            ).map((recent) => {
              const match = recent && recent.id
                ? catalogueByVideoId.get(recent.id)
                : null;

              if (!match) return recent;

              return {
                ...match.video,
                ...recent,
                _catalogue_channel_id: match.catalogueChannelId,
                _catalogue_index: match.catalogueIndex,
              };
            });

            return (
              <UpdatesOverlay
                notes={updatesData.notes}
                features={updatesData.features}
                recentVideos={enrichedRecentVideos}
                onClose={() => setMode("tv")}
                onTuneToVideo={(v) => {
                  if (!v || !v.id) return;

                  let destinationChannelId = v._catalogue_channel_id || null;
                  let destinationIndex = Number.isInteger(v._catalogue_index)
                    ? v._catalogue_index
                    : -1;

                  if (!destinationChannelId || destinationIndex < 0) {
                    for (const [candidateChannelId, candidateVideos] of Object.entries(videosByChannel)) {
                      const candidateIndex = Array.isArray(candidateVideos)
                        ? candidateVideos.findIndex((x) => x && x.id === v.id)
                        : -1;
                      if (candidateIndex >= 0) {
                        destinationChannelId = candidateChannelId;
                        destinationIndex = candidateIndex;
                        break;
                      }
                    }
                  }

                  if (destinationChannelId && destinationIndex >= 0) {
                    tuneToChannel(destinationChannelId, destinationIndex);
                    setMode("tv");
                  }
                }}
              />
            );
          })()}
          {/* Dismissable broadcast notice — must be acked before videos
              play. Renders ABOVE the mode views (z-50) but BELOW
              maintenance (z-55), so if both fire, maintenance wins. The
              auto-power effect above forces the CRT on so non-owners
              with TV off can still see the message. */}
          {power && notice && notice.enabled && noticeAckedId !== notice.notice_id && !maintenance && (
            <NoticeOverlay
              title={notice.title}
              body={notice.body}
              ctaLabel={notice.cta_label}
              validUntil={notice.valid_until}
              onAcknowledge={handleAcknowledgeNotice}
            />
          )}
          {/* EPG-update / maintenance mode — gates only non-owner visitors.
              Highest priority inside the cabinet (z-55); covers every mode
              and the notice screen. Owners keep the full interface so they
              can finish what they're doing. */}
          {power && maintenance && !isOwner && ownerChecked && (
            <MaintenanceOverlay message={maintenanceMsg} returnsAt={maintenanceReturnsAt} />
          )}
        </CRT>

        <SharePanel
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          currentChannel={currentChannel}
          currentVideo={currentVideo}
          apiBase={API}
          onShared={() => fireTrophy("broadcaster")}
        />

        <div
          id="lobby-dock"
          className="fixed inset-y-0 right-0 z-[70] pointer-events-none"
          data-testid="lobby-dock"
          aria-label="SCARTLEAD.TV Lobby"
        />
        {ownerChecked && (isOwner || (maintenanceChecked && !maintenance)) && (
    <Remote
          power={power}
          isOwner={isOwner}
          onPower={togglePower}
          onPowerLongPress={handleCycleScanlines}
          onChannelUp={dpadUp}
          onChannelDown={dpadDown}
          onMenu={openUpload}
          onShare={handleShare}
          onGuide={toggleGuide}
          onUpdates={handleOpenUpdates}
          updatesAvailable={updatesAvailable}
          onSelect={handleSelectInfo}
          onSelectLongPress={handleSelectPeek}
          onDigit={pushDigit}
          onLast={tuneToLast}
          onDpadUp={dpadUp}
          onDpadDown={dpadDown}
          onDpadLeft={() => {
            if (mode === "guide") window.dispatchEvent(new CustomEvent("crt:guide-nav", { detail: { direction: "left" } }));
            else stepVideo(-1);
          }}
          onDpadRight={() => {
            if (mode === "guide") window.dispatchEvent(new CustomEvent("crt:guide-nav", { detail: { direction: "right" } }));
            else stepVideo(1);
          }}
          digitBuffer={digitBuffer}
          currentChannelNumber={power && currentChannel && !currentChannel.virtual ? currentChannel.number : null}
          playing={power && !remotePaused && !!currentChannel && !currentChannel.virtual}
          paused={remotePaused}
          volume={volume}
          onVolumeDown={() => changeRemoteVolume(-1)}
          onVolumeUp={() => changeRemoteVolume(1)}
          onPlayPause={handleRemotePlayPause}
          onPictureMode={cyclePictureMode}
          brandText={theme.brand_text}
          callsign={theme.callsign}
        />
    )}
        {ownerChecked && (isOwner || (maintenanceChecked && !maintenance)) && (
          <MobileTouchRemote
            power={power}
            paused={remotePaused}
            volume={volume}
            updatesAvailable={updatesAvailable}
            guideMode={mode === "guide"}
            onChannelDown={dpadDown}
            onChannelUp={dpadUp}
            onGuideLeft={() => window.dispatchEvent(new CustomEvent("crt:guide-nav", { detail: { direction: "left" } }))}
            onGuideRight={() => window.dispatchEvent(new CustomEvent("crt:guide-nav", { detail: { direction: "right" } }))}
            onPlayPause={handleRemotePlayPause}
            onVolumeDown={() => changeRemoteVolume(-1)}
            onVolumeUp={() => changeRemoteVolume(1)}
            onGuide={toggleGuide}
            onSelect={handleSelectInfo}
            onLast={tuneToLast}
            onPictureMode={cyclePictureMode}
            onUpdates={handleOpenUpdates}
            onPower={togglePower}
          />
        )}
      </div>
    </div>
  );
}
